import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Calendar, Target, Sparkles, Search, Filter, Users, Building2, X } from 'lucide-react';
import { jwtDecode } from "jwt-decode";
import AlertBox from '../../Components/AlertBox';
import apiService from '../services/api';
const FollowUpTasks = ({ onNavigate }) => {
  const navigate = useNavigate();
  const [allFollowups, setAllFollowups] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [localProjects, setLocalProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusOptions, setStatusOptions] = useState(['Pending', 'In Progress', 'Completed', 'Overdue', 'Late Completed']);
  const [statusFilter, setStatusFilter] = useState('all');
  // Change Status modal state
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusForm, setStatusForm] = useState({ milestoneId: null, status: '', description: '', files: [], urlInput: '', urlList: [] });
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const MAX_STATUS_FILE_BYTES = 5 * 1024 * 1024; // 5MB per file
  const ALLOWED_STATUS_MIME = [
    'image/jpeg',
    'image/png',
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain'
  ];

  // New filter states
  const [departments, setDepartments] = useState([]);
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [activeMobileDropdown, setActiveMobileDropdown] = useState(null); // 'department' | 'employee' | 'status' | null
  const [showAlert, setShowAlert] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [statusUpdates, setStatusUpdates] = useState({});
  const [imageViewer, setImageViewer] = useState({ open: false, items: [], index: 0 });

  // Filter employees based on selected department
  const getFilteredEmployees = () => {
    // First filter: Only active employees
    const activeEmployees = employees.filter(emp =>
      emp.isActive !== false && emp.status !== 'Inactive' && emp.status !== 'Deactive'
    );

    if (departmentFilter === 'all') {
      return activeEmployees; // Show all active employees when no department is selected
    }

    // Find active employees who belong to the selected department or work on projects in that department
    const filteredEmployees = activeEmployees.filter(employee => {
      // Check if employee's direct department matches
      if (employee.department || employee.departmentId) {
        const empDeptId = employee.department || employee.departmentId;

        // Handle employee department as object
        if (typeof empDeptId === 'object' && empDeptId.name) {
          if (empDeptId.name === departmentFilter) return true;
        }

        // Handle employee department as ID - find department by ID
        if (typeof empDeptId === 'string') {
          const empDept = departments.find(d => d._id === empDeptId || d.id === empDeptId);
          if (empDept && empDept.name === departmentFilter) return true;
        }
      }

      // Check if employee works on any project in the selected department
      const employeeProjects = localProjects.filter(project => {
        return project.teamMembers && project.teamMembers.some(member => {
          if (!member || !employee) return false;
          const memberId = typeof member === 'object' ? (member._id || member.id) : member;
          const employeeId = typeof employee === 'object' ? (employee._id || employee.id) : employee;
          return String(memberId) === String(employeeId);
        });
      });

      // Check if any of the employee's projects belong to the selected department
      const hasProjectInDepartment = employeeProjects.some(project => {
        if (!project.department) return false;

        if (Array.isArray(project.department)) {
          return project.department.some(dept => {
            if (typeof dept === 'object' && dept.name) {
              return dept.name === departmentFilter;
            } else if (typeof dept === 'string') {
              const foundDept = departments.find(d => d._id === dept || d.id === dept);
              return foundDept && foundDept.name === departmentFilter;
            }
            return false;
          });
        } else {
          if (typeof project.department === 'object' && project.department.name) {
            return project.department.name === departmentFilter;
          } else if (typeof project.department === 'string') {
            const foundDept = departments.find(d => d._id === project.department || d.id === project.department);
            return foundDept && foundDept.name === departmentFilter;
          }
        }
        return false;
      });

      return hasProjectInDepartment;
    });

    console.log(`👥 Filtered Active Employees for Department "${departmentFilter}":`, {
      selectedDepartment: departmentFilter,
      totalEmployees: employees.length,
      activeEmployees: activeEmployees.length,
      filteredEmployees: filteredEmployees.length,
      employeeNames: filteredEmployees.map(emp => emp.teamMemberName || emp.name || 'Unknown')
    });

    return filteredEmployees;
  };

  // Open task detail modal on card click
  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setShowTaskModal(true);
  };

  // ------ Delete Task Handler ------
  const handleDeleteTask = async (milestoneId) => {
    try {
      console.log('—ï Deleting task:', milestoneId);
      await apiService.deleteMilestone(milestoneId);

      // Refresh the task list
      await fetchFollowups();

      // Close the task modal
      setShowTaskModal(false);
      setSelectedTask(null);

      setShowAlert('✅ Task deleted successfully!');
      console.log('✅ Task deleted');
    } catch (error) {
      console.error('❌ Error deleting task:', error);
      setShowAlert('❌ Failed to delete task: ' + error.message);
    }
  };

  // ------ Change Status Handlers ------
  const openTaskStatusModal = (milestoneId) => {
    console.log('FollowUpTasks - openTaskStatusModal called with milestoneId:', milestoneId);
    // Try to pre-fill the status from the selected milestone so the user
    // doesn't have to re-select it every time (prevents silent 'no-status' failures)
    const existing = allFollowups.find(m => (m && (m._id === milestoneId || m.id === milestoneId)));
    const preStatus = existing ? (existing.status || calculateDynamicStatus(existing) || '') : '';
    if (!preStatus) console.warn('FollowUpTasks - No existing status found to prefill for', milestoneId);

    // Ensure selectedTask is set so the modal has the task context (helps rendering and updates)
    if (existing) {
      setSelectedTask(existing);
    } else {
      // Best-effort: fetch the milestone from the API so we have the full object
      (async () => {
        try {
          const fetched = await apiService.getMilestone(milestoneId);
          if (fetched) setSelectedTask(fetched);
        } catch (err) {
          console.warn('FollowUpTasks - Could not fetch milestone for modal prefill', err);
        }
      })();
    }

    setStatusForm({ milestoneId, status: preStatus, description: '', files: [], urlInput: '', urlList: [] });
    setShowStatusModal(true);
  };

  // Helper: run a promise with a timeout to avoid UI getting stuck
  const withTimeout = (promise, ms = 25000) => {
    let timer;
    return Promise.race([
      promise,
      new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error('timeout')), ms);
      })
    ]).finally(() => clearTimeout(timer));
  };

  const handleStatusFileChange = (e) => {
    const incoming = Array.from(e.target.files || []);
    const accepted = [];
    const rejected = [];

    incoming.forEach((file) => {
      const typeOk =
        ALLOWED_STATUS_MIME.includes(file.type) ||
        /(png|jpe?g|pdf|docx|txt)$/i.test(file.name);

      const sizeOk = file.size <= MAX_STATUS_FILE_BYTES;

      if (typeOk && sizeOk) {
        accepted.push(file);
      } else {
        rejected.push({
          name: file.name,
          reason: !typeOk ? "Invalid file type" : "File size exceeds 5 MB",
        });
      }
    });

    if (rejected.length > 0) {
      const msg = rejected
        .map((r) => `${r.name}: ${r.reason}`)
        .join("\n");

      alert("Some files were rejected:\n\n" + msg);
    }

    setStatusForm((prev) => ({ ...prev, files: accepted }));
  };


  // In your FollowUpTasks component, update the submitTaskStatusChange function:
  const submitTaskStatusChange = async () => {
    console.log(' submitTaskStatusChange - Starting update process', {
      milestoneId: statusForm.milestoneId,
      status: statusForm.status,
      description: statusForm.description,
      hasFiles: statusForm.files?.length > 0,
      hasUrls: statusForm.urlList?.length > 0
    });

    // VALIDATION
    if (!statusForm.milestoneId) {
      const errorMsg = 'Missing milestone ID. Cannot update task.';
      console.error('❌', errorMsg);
      setShowAlert(errorMsg);
      throw new Error(errorMsg);
    }

    if (!statusForm.status) {
      const errorMsg = 'Please select a status before updating.';
      console.warn('⚠️', errorMsg);
      setShowAlert(errorMsg);
      throw new Error(errorMsg);
    }

    setUploadingMedia(true);

    try {
      // 1. Get existing milestone data
      let existing = null;
      try {
        existing = await apiService.getMilestone(statusForm.milestoneId);
        console.log('✅ Found existing milestone:', existing);
      } catch (fetchError) {
        console.warn('⚠️ Could not fetch existing milestone, proceeding with update anyway:', fetchError);
      }

      // 2. Prepare description/notes arrays - FIXED LOGIC
      const prevNotes = (() => {
        if (!existing) return [];

        // Check multiple possible fields for existing notes
        const raw = existing.statusNote || existing.description || existing.notes || [];

        if (Array.isArray(raw)) {
          return raw.filter(item => item && typeof item === 'string' && item.trim() !== '');
        }

        if (typeof raw === 'string' && raw.trim()) {
          return [raw.trim()];
        }

        return [];
      })();

      console.log(' Previous notes:', prevNotes);

      // Prepare new description as plain text (not array)
      const newDescription = statusForm.description
        ? statusForm.description.trim()
        : '';

      console.log(' New description (plain text):', newDescription);

      // 3. Prepare update payload - Send description as plain text
      const updateData = {
        status: statusForm.status,
      };

      // Add description as plain text if provided
      if (newDescription) {
        updateData.description = newDescription;
        console.log('✅ Added description as plain text to update payload');
      }

      // 4. Handle URLs if provided
      if ((statusForm.urlList || []).length > 0) {
        const existingImages = Array.isArray(existing?.images) ? existing.images : [];
        const existingAttachments = Array.isArray(existing?.attachmentUrls) ? existing.attachmentUrls : [];
        const newMediaUrls = statusForm.urlList || [];

        updateData.images = [...new Set([...existingImages, ...newMediaUrls])];
        updateData.attachmentUrls = [...new Set([...existingAttachments, ...newMediaUrls])];
      }

      console.log('📦 Final update payload:', updateData);

      let updateResult;

      // 5. DECISION: Use JSON update or FormData based on files
      if (statusForm.files && statusForm.files.length > 0) {
        // Use FormData for file uploads
        console.log('Ž Using FormData update with files...');

        // Create clean update data without empty arrays for FormData
        const formDataUpdate = { ...updateData };

        // Remove empty arrays for FormData to avoid JSON stringification issues
        Object.keys(formDataUpdate).forEach(key => {
          if (Array.isArray(formDataUpdate[key]) && formDataUpdate[key].length === 0) {
            delete formDataUpdate[key];
          }
        });

        updateResult = await apiService.updateMilestoneWithFiles(
          statusForm.milestoneId,
          formDataUpdate,
          statusForm.files
        );
        console.log('✅ FormData update successful');
      } else {
        // Use simple JSON update (faster)
        console.log('„ Using simple JSON update...');

        // Clean up data for JSON update - remove empty arrays
        const jsonUpdate = { ...updateData };
        Object.keys(jsonUpdate).forEach(key => {
          if (Array.isArray(jsonUpdate[key]) && jsonUpdate[key].length === 0) {
            delete jsonUpdate[key];
          }
        });

        updateResult = await apiService.updateMilestone(statusForm.milestoneId, jsonUpdate);
        console.log('✅ JSON update successful');
      }

      // 6. Refresh data and update UI
      try {
        console.log('„ Refreshing data...');

        // First refresh the followups list
        await fetchFollowups();

        // Then refresh the selected task to show updated history
        if (selectedTask && (selectedTask._id === statusForm.milestoneId || selectedTask.id === statusForm.milestoneId)) {
          console.log('„ Refreshing selected task...');
          const response = await apiService.getMilestone(statusForm.milestoneId);
          // Backend returns { milestone: {...} }
          const refreshed = response.milestone || response;
          console.log('✅ Refreshed task data:', refreshed);
          console.log('📊 Status History after refresh:', refreshed?.statusHistory);
          setSelectedTask(refreshed);
        }
        console.log('✅ Data refresh complete');
      } catch (refreshErr) {
        console.error('⚠️ Could not refresh data:', refreshErr);
      }

      // 7. Clear local state for this task (data is now in server)
      // The history will be fetched from server on next load
      setStatusUpdates(prev => {
        const updated = { ...prev };
        delete updated[statusForm.milestoneId];
        return updated;
      });

      // 8. Close status modal (keep task modal open) and show success
      setShowStatusModal(false);
      setShowAlert('✅ Task updated successfully! Check Update History below.');
      console.log('🎉 Task status change completed successfully');

    } catch (err) {
      console.error('❌ submitTaskStatusChange - Final error:', err);
      const errorMsg = err.message || 'Failed to update task. Please try again.';
      setShowAlert(errorMsg);
      throw err;
    } finally {
      setUploadingMedia(false);
    }
  };
  // Filter statuses based on selected department
  const getFilteredStatuses = () => {
    if (departmentFilter === 'all') {
      return statusOptions; // Show all statuses when no department is selected
    }

    // Find all tasks that belong to the selected department
    const departmentTasks = allFollowups.filter(followup => {
      const projectForTask = localProjects.find(p =>
        (p.title === followup.projectName) ||
        (p._id === followup.projectId) ||
        (p.title === getProjectNameForMilestone(followup))
      );

      if (!projectForTask || !projectForTask.department) {
        return false;
      }

      // Check if project belongs to selected department
      if (Array.isArray(projectForTask.department)) {
        return projectForTask.department.some(dept => {
          if (typeof dept === 'object' && dept.name) {
            return dept.name === departmentFilter;
          } else if (typeof dept === 'string') {
            const foundDept = departments.find(d => d._id === dept || d.id === dept);
            return foundDept && foundDept.name === departmentFilter;
          }
          return false;
        });
      } else {
        if (typeof projectForTask.department === 'object' && projectForTask.department.name) {
          return projectForTask.department.name === departmentFilter;
        } else if (typeof projectForTask.department === 'string') {
          const foundDept = departments.find(d => d._id === projectForTask.department || d.id === projectForTask.department);
          return foundDept && foundDept.name === departmentFilter;
        }
      }
      return false;
    });

    // Get unique statuses from tasks in this department
    const departmentStatuses = new Set();
    departmentTasks.forEach(task => {
      const dynamicStatus = calculateDynamicStatus(task);
      departmentStatuses.add(dynamicStatus);
    });

    // Convert to array and sort to match original order
    const availableStatuses = statusOptions.filter(status => departmentStatuses.has(status));

    console.log(`📊 Filtered Statuses for Department "${departmentFilter}":`, {
      selectedDepartment: departmentFilter,
      totalStatuses: statusOptions.length,
      availableStatuses: availableStatuses.length,
      statusList: availableStatuses,
      departmentTasks: departmentTasks.length
    });

    return availableStatuses;
  };

  // Get company ID from JWT token (same approach as AddProject)
  const getCompanyId = () => {
    const token = localStorage.getItem('authToken');
    let COMPANY_ID = '';
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        // Handle both company and employee login types
        COMPANY_ID = decodedToken.role === 'company'
          ? (decodedToken.companyId || decodedToken.id)
          : decodedToken.companyId;
        console.log("FollowUpTasks - Decoded Token:", decodedToken);
        console.log("FollowUpTasks - Extracted Company ID:", COMPANY_ID);
        console.log("FollowUpTasks - User Role:", decodedToken.role);
        return COMPANY_ID;
      } catch (err) {
        console.error("FollowUpTasks - Error decoding token:", err);
        return '';
      }
    }
    return '';
  };


  // Function to fetch departments from API (same approach as AddProject)
  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const COMPANY_ID = getCompanyId();
      console.log("FollowUpTasks - Fetching departments for company:", COMPANY_ID);

      let departmentData = null;

      // First try: with company ID and auth (same as AddProject)
      if (COMPANY_ID) {
        try {
          const res = await fetch(`https://slateblue-mallard-430836.hostingersite.com/api/departments/company/${COMPANY_ID}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          if (res.ok) {
            departmentData = await res.json();
            console.log("FollowUpTasks - Departments fetched with auth:", departmentData?.length);
          }
        } catch (error) {
          console.log("FollowUpTasks - Auth department fetch failed, trying alternatives");
        }

        // Second try: without auth for company access
        if (!departmentData) {
          try {
            const res = await fetch(`https://slateblue-mallard-430836.hostingersite.com/api/departments/company/${COMPANY_ID}`);
            if (res.ok) {
              departmentData = await res.json();
              console.log("FollowUpTasks - Departments fetched without auth:", departmentData?.length);
            }
          } catch (error) {
            console.log("FollowUpTasks - No-auth department fetch failed");
          }
        }
      }

      // Third try: general departments endpoint
      if (!departmentData) {
        try {
          const res = await fetch('https://slateblue-mallard-430836.hostingersite.com/api/departments', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          if (res.ok) {
            departmentData = await res.json();
            console.log("FollowUpTasks - General departments fetched:", departmentData?.length);

            // Filter by company if we have company ID
            if (COMPANY_ID && Array.isArray(departmentData)) {
              departmentData = departmentData.filter(dept =>
                dept.company === COMPANY_ID || dept.companyId === COMPANY_ID
              );
              console.log("FollowUpTasks - Filtered departments by company:", departmentData?.length);
            }
          }
        } catch (error) {
          console.log("FollowUpTasks - General department fetch failed");
        }
      }

      setDepartments(departmentData || []);

      // Debug logging for departments
      console.log('Departments loaded for filtering:', {
        count: (departmentData || []).length,
        departments: (departmentData || []).map(d => ({ id: d._id, name: d.name }))
      });
    } catch (error) {
      console.error('FollowUpTasks - Error fetching departments:', error);
      setDepartments([]);
    }
  };

  const fetchFollowups = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('https://slateblue-mallard-430836.hostingersite.com/api/milestones', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const rawMilestones = data.milestones || data;
        // Filter out null/undefined/invalid objects
        let milestones = Array.isArray(rawMilestones)
          ? rawMilestones.filter(m => m && typeof m === 'object' && m._id)  // Ensure it's a valid object with an _id
          : [];

        console.log('FollowUpTasks - Fetched milestones (before filter):', milestones.length);

        // Filter milestones based on user role
        let decodedToken;
        try {
          decodedToken = jwtDecode(token);
        } catch (e) {
          console.error('FollowUpTasks - Error decoding token:', e);
        }

        if (decodedToken) {
          const userRole = decodedToken.role;
          const userId = decodedToken.id || decodedToken._id;
          console.log('FollowUpTasks - User role:', userRole, 'User ID:', userId);

          // Normalize role to lowercase for comparison
          const normalizedRole = userRole ? userRole.toLowerCase() : '';

          if (normalizedRole === 'employee' || normalizedRole === 'team_member') {
            console.log('FollowUpTasks - Applying filter for employee/team_member');

            const originalCount = milestones.length;

            milestones = milestones.filter(milestone => {
              // Check if user is assigned to this task
              const assignedTeamMember = milestone.assignedTeamMember;

              if (!assignedTeamMember) {
                // console.log(`FollowUpTasks - Task "${milestone.title}" - No assigned members`);
                return false;
              }

              // Handle array of assigned members
              if (Array.isArray(assignedTeamMember)) {
                const isAssigned = assignedTeamMember.some(member => {
                  if (!member) return false;
                  const memberId = typeof member === 'object' ? (member._id || member.id) : member;
                  // console.log(`FollowUpTasks - Checking member:`, memberId, 'against', userId);
                  return String(memberId) === String(userId);
                });
                if (isAssigned) console.log(`FollowUpTasks - Task "${milestone.title}" - MATCH FOUND (Array)`);
                return isAssigned;
              }

              // Handle single assigned member
              const memberId = typeof assignedTeamMember === 'object' ?
                (assignedTeamMember._id || assignedTeamMember.id) : assignedTeamMember;

              const isAssigned = String(memberId) === String(userId);
              if (isAssigned) console.log(`FollowUpTasks - Task "${milestone.title}" - MATCH FOUND (Single)`);
              return isAssigned;
            });

            console.log(`FollowUpTasks - Filtering complete. Kept ${milestones.length} out of ${originalCount} tasks.`);
          } else {
            console.log('FollowUpTasks - User is not employee/team_member, showing all tasks');
          }
        }

        setAllFollowups(milestones);
      } else {
        console.error('FollowUpTasks - Failed to fetch milestones:', response.status);
        setAllFollowups([]);
      }
    } catch (error) {
      console.error('FollowUpTasks - Error fetching milestones:', error);
      setAllFollowups([]);
    }
  };


  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const COMPANY_ID = getCompanyId();
      console.log("FollowUpTasks - Fetching employees for company:", COMPANY_ID);

      let employeeData = null;

      // First try: company-specific endpoint with auth (same as AddProject)
      if (COMPANY_ID) {
        try {
          const res = await fetch(`https://slateblue-mallard-430836.hostingersite.com/api/employees/company/${COMPANY_ID}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (res.ok) {
            employeeData = await res.json();
            console.log("FollowUpTasks - Employees fetched:", employeeData?.length, "employees");
          }
        } catch (error) {
          console.log("FollowUpTasks - Company-specific employee fetch failed");
        }
      }

      // Fallback strategies (same as AddProject)
      if (!employeeData) {
        // Try general employees endpoint with auth
        try {
          const fallbackRes = await fetch('https://slateblue-mallard-430836.hostingersite.com/api/employees', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          if (fallbackRes.ok) {
            employeeData = await fallbackRes.json();
            console.log("FollowUpTasks - Auth fallback employees fetched:", employeeData?.length);

            // Filter by company if we have company ID
            if (COMPANY_ID && Array.isArray(employeeData)) {
              employeeData = employeeData.filter(emp =>
                emp.company === COMPANY_ID || emp.companyId === COMPANY_ID
              );
              console.log("FollowUpTasks - Filtered employees by company:", employeeData?.length);
            }
          }
        } catch (error) {
          console.log("FollowUpTasks - Auth fallback failed, trying no-auth");
        }

        // Try no-auth fallback for company access
        if (!employeeData) {
          try {
            const noAuthRes = await fetch('https://slateblue-mallard-430836.hostingersite.com/api/employees');
            if (noAuthRes.ok) {
              employeeData = await noAuthRes.json();
              console.log("FollowUpTasks - No-auth fallback employees fetched:", employeeData?.length);

              // Filter by company if we have company ID
              if (COMPANY_ID && Array.isArray(employeeData)) {
                employeeData = employeeData.filter(emp =>
                  emp.company === COMPANY_ID || emp.companyId === COMPANY_ID
                );
                console.log("FollowUpTasks - Filtered employees by company:", employeeData?.length);
              }
            }
          } catch (error) {
            console.log("FollowUpTasks - No-auth fallback failed");
          }
        }
      }

      setEmployees(employeeData || []);
    } catch (error) {
      console.error('FollowUpTasks - Error fetching employees:', error);
      setEmployees([]);
    }
  };

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('https://slateblue-mallard-430836.hostingersite.com/api/projectsmgnt', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        let data = await response.json();
        let projectList = Array.isArray(data) ? data : [];
        console.log('FollowUpTasks - Fetched projects (before filter):', projectList.length);

        // Filter projects based on user role
        const decodedToken = jwtDecode(token);
        const userRole = decodedToken.role;
        const userId = decodedToken.id || decodedToken._id;
        console.log('FollowUpTasks - User role:', userRole, 'User ID:', userId);

        if (userRole === 'employee' || userRole === 'team_member') {
          projectList = projectList.filter(project => {
            const isTeamMember = project.teamMembers && Array.isArray(project.teamMembers) &&
              project.teamMembers.some(member => {
                const memberId = typeof member === 'object' ? (member._id || member.id) : member;
                return String(memberId) === String(userId);
              });

            console.log(`FollowUpTasks - Project "${project.title || project.name}" - isTeamMember:`, isTeamMember);
            return isTeamMember;
          });

          console.log('FollowUpTasks - Filtered projects for team member:', projectList.length);
        }

        setLocalProjects(projectList);

        // Debug logging for projects and their departments
        console.log('—ï Projects loaded with department info:', {
          count: projectList.length,
          projectsWithDepartments: projectList.filter(p => p.department).map(p => ({
            title: p.title,
            department: p.department,
            teamMembers: p.teamMembers?.length || 0
          }))
        });
      } else {
        console.error('FollowUpTasks - Failed to fetch projects:', response.status);
        setLocalProjects([]);
      }
    } catch (error) {
      console.error('FollowUpTasks - Error fetching projects:', error);
      setLocalProjects([]);
    }
  };

  // Initial data fetch
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      // Fetch all data (company filtering is now handled within each fetch function)
      await Promise.all([
        fetchFollowups(),
        fetchEmployees(),
        fetchProjects(),
        fetchDepartments()
      ]);
      setLoading(false);
    };

    fetchData();
  }, []);

  // Helper functions
  const getEmployeeName = (assignedTeamMember) => {
    if (!assignedTeamMember) {
      return 'Unassigned';
    }

    // Handle case where assignedTeamMember is an object
    if (typeof assignedTeamMember === 'object') {
      return assignedTeamMember.teamMemberName || assignedTeamMember.name || 'Unknown';
    }

    // Handle case where assignedTeamMember is an ID string
    const employee = employees.find(emp => emp._id === assignedTeamMember);
    return employee ? (employee.teamMemberName || employee.name || 'Unknown') : 'Unknown Employee';
  };

  const getEmployeeById = (assignedTeamMember) => {
    if (!assignedTeamMember) return null;

    // Handle case where assignedTeamMember is an object
    if (typeof assignedTeamMember === 'object') {
      return assignedTeamMember;
    }

    // Handle case where assignedTeamMember is an ID string
    return employees.find(emp => emp._id === assignedTeamMember);
  };

  // Dynamic status calculation - respects manually set status
  const calculateDynamicStatus = (followup) => {
    const currentDate = new Date();
    const dueDate = followup.dueDate ? new Date(followup.dueDate) : null;
    const status = followup.status || 'Pending';

    // Always respect the manually set status from the database
    // Only add "Late Completed" logic for completed tasks
    if (status === 'Completed') {
      if (dueDate && currentDate > dueDate) {
        return 'Late Completed';
      }
      return 'Completed';
    }

    // For all other statuses (In Progress, Pending, Overdue, etc.)
    // Return the status as set by the user - don't override it
    return status;
  };

  // Status badge styles (same as ProjectView)
  const statusBadgeBase = {
    padding: '0.25rem 0.75rem',
    borderRadius: '50px',
    fontSize: '0.8rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease'
  };

  const getStatusBadgeStyle = (status) => {
    switch (status) {
      case 'Completed': return { ...statusBadgeBase, background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white' };
      case 'Late Completed': return { ...statusBadgeBase, background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: 'white' };
      case 'Overdue': return { ...statusBadgeBase, background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', color: 'white' };
      case 'In Progress': return { ...statusBadgeBase, background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', color: 'white' };
      case 'Pending': return { ...statusBadgeBase, background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)', color: 'white' };
      default: return { ...statusBadgeBase, background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)', color: 'white' };
    }
  };

  // Helper function to get department from milestone/task data
  const getDepartmentFromTask = (followup) => {
    // Check if task has direct department information
    if (followup.department) {
      if (typeof followup.department === 'string') {
        const dept = departments.find(d =>
          String(d._id) === String(followup.department) ||
          d.name === followup.department
        );
        return dept ? dept.name : followup.department;
      } else if (typeof followup.department === 'object' && followup.department.name) {
        return followup.department.name;
      }
    }

    // Check if task has departmentName
    if (followup.departmentName) {
      return followup.departmentName;
    }

    return null;
  };

  const getEmployeeDepartment = (assignedTeamMember, followup = null) => {
    const employee = getEmployeeById(assignedTeamMember);
    if (!employee) return 'Unassigned';

    console.log(`Getting Department for Employee:`, {
      employeeName: employee.teamMemberName || employee.name,
      employeeId: employee._id,
      employeeDepartment: employee.department,
      employeeDepartmentId: employee.departmentId,
      employeeDepartmentName: employee.departmentName,
      employeeRole: employee.role,
      employeePosition: employee.position,
      isEmployeeDepartmentArray: Array.isArray(employee.department),
      availableDepartments: departments.map(d => ({ id: d._id, name: d.name })),
      taskProject: followup ? getProjectNameForMilestone(followup) : 'No task provided'
    });

    // Collect all possible departments for this employee
    const foundDepartments = new Set();

    // First try to get department from employee data
    let departmentId = employee.department || employee.departmentId;

    // Handle case where department is an array (multiple departments)
    if (Array.isArray(departmentId)) {
      departmentId.forEach(deptId => {
        const id = typeof deptId === 'object' ? (deptId._id || deptId.id) : deptId;
        const department = departments.find(dept =>
          String(dept._id) === String(id) ||
          String(dept.id) === String(id)
        );
        if (department) {
          foundDepartments.add(department.name);
        }
      });
    } else if (departmentId) {
      // Handle case where department is an object with _id
      if (typeof departmentId === 'object') {
        departmentId = departmentId._id || departmentId.id;
      }

      const department = departments.find(dept =>
        String(dept._id) === String(departmentId) ||
        String(dept.id) === String(departmentId)
      );
      if (department) {
        foundDepartments.add(department.name);
      }
    }

    // If we found departments from employee data, check if we should return them
    if (foundDepartments.size > 0) {
      console.log(`✅ Found departments from employee:`, Array.from(foundDepartments));
    }

    // Find ALL projects this employee is assigned to (not just the current task's project)
    const employeeProjects = localProjects.filter(project => {
      return project.teamMembers && project.teamMembers.some(member => {
        const memberId = typeof member === 'object' ? (member._id || member.id) : member;
        const employeeId = typeof employee === 'object' ? (employee._id || employee.id) : employee;
        return String(memberId) === String(employeeId);
      });
    });

    console.log(`—ï Employee Projects Found:`, {
      employeeName: employee.teamMemberName || employee.name,
      projectCount: employeeProjects.length,
      projects: employeeProjects.map(p => ({
        title: p.title,
        department: p.department,
        departmentType: typeof p.department,
        isArray: Array.isArray(p.department)
      }))
    });

    // Collect departments from ALL projects the employee is assigned to
    employeeProjects.forEach(project => {
      if (project.department) {
        if (Array.isArray(project.department)) {
          project.department.forEach(dept => {
            if (typeof dept === 'object' && dept.name) {
              foundDepartments.add(dept.name);
            } else if (typeof dept === 'string') {
              const foundDept = departments.find(d =>
                String(d._id) === String(dept) ||
                String(d.id) === String(dept)
              );
              if (foundDept) {
                foundDepartments.add(foundDept.name);
              }
            }
          });
        } else {
          // Single department from project
          let deptId = project.department;

          if (typeof project.department === 'object' && project.department.name) {
            foundDepartments.add(project.department.name);
          } else if (typeof project.department === 'object' && (project.department._id || project.department.id)) {
            deptId = project.department._id || project.department.id;
          }

          if (deptId && typeof deptId === 'string') {
            const foundDept = departments.find(d =>
              String(d._id) === String(deptId) ||
              String(d.id) === String(deptId)
            );
            if (foundDept) {
              foundDepartments.add(foundDept.name);
            }
          }
        }
      }
    });

    // Find the specific project this task belongs to (for logging)
    const taskProject = localProjects.find(project => {
      // Check if this employee is assigned to this project
      return project.teamMembers && project.teamMembers.some(member => {
        const memberId = typeof member === 'object' ? (member._id || member.id) : member;
        const employeeId = typeof employee === 'object' ? (employee._id || employee.id) : employee;
        return String(memberId) === String(employeeId);
      });
    });

    console.log(`—ï Task Project Found:`, {
      projectFound: !!taskProject,
      projectTitle: taskProject?.title,
      projectDepartment: taskProject?.department,
      projectDepartmentType: typeof taskProject?.department
    });

    if (taskProject && taskProject.department) {
      // Handle project department (can be array or single value)
      if (Array.isArray(taskProject.department)) {
        // Add all departments from project to our collection
        taskProject.department.forEach(dept => {
          if (typeof dept === 'object' && dept.name) {
            foundDepartments.add(dept.name);
          } else if (typeof dept === 'string') {
            const foundDept = departments.find(d =>
              String(d._id) === String(dept) ||
              String(d.id) === String(dept)
            );
            if (foundDept) {
              foundDepartments.add(foundDept.name);
            }
          }
        });

        // Debug logging for multiple departments from project
        console.log(`Multiple Departments from Project:`, {
          employeeName: employee.teamMemberName || employee.name,
          projectTitle: taskProject.title,
          projectDepartments: taskProject.department,
          addedDepartments: Array.from(foundDepartments)
        });
      } else {
        // Single department from project
        let deptId = taskProject.department;

        // Handle department as object
        if (typeof taskProject.department === 'object' && taskProject.department.name) {
          foundDepartments.add(taskProject.department.name);
        } else if (typeof taskProject.department === 'object' && (taskProject.department._id || taskProject.department.id)) {
          deptId = taskProject.department._id || taskProject.department.id;
        }

        // Handle department as string ID
        if (deptId) {
          const foundDept = departments.find(d =>
            String(d._id) === String(deptId) ||
            String(d.id) === String(deptId)
          );
          if (foundDept) {
            foundDepartments.add(foundDept.name);
          }
        }

        // Debug logging for single department from project
        console.log(`Single Department from Project:`, {
          employeeName: employee.teamMemberName || employee.name,
          projectTitle: taskProject.title,
          projectDepartment: taskProject.department,
          departmentId: deptId,
          addedDepartments: Array.from(foundDepartments)
        });
      }
    }

    // Try alternative approach: check if employee has department name directly
    if (employee.departmentName) {
      console.log(` Adding employee departmentName:`, employee.departmentName);
      foundDepartments.add(employee.departmentName);
    }

    // Try to get department from the task/milestone itself
    if (followup) {
      const taskDepartment = getDepartmentFromTask(followup);
      if (taskDepartment) {
        console.log(`📋 Adding department from task:`, taskDepartment);
        foundDepartments.add(taskDepartment);
      }
    }

    // Try to find department by employee's company and role
    if (employee.role || employee.position) {
      const roleBasedDept = departments.find(dept =>
        dept.name && (
          dept.name.toLowerCase().includes((employee.role || '').toLowerCase()) ||
          dept.name.toLowerCase().includes((employee.position || '').toLowerCase())
        )
      );
      if (roleBasedDept) {
        console.log(`🎯 Adding role-based department:`, roleBasedDept.name);
        foundDepartments.add(roleBasedDept.name);
      }
    }


    // Return all found departments as comma-separated string
    if (foundDepartments.size > 0) {
      const result = Array.from(foundDepartments).join(', ');
      console.log(`✅ Final departments result:`, {
        employeeName: employee.teamMemberName || employee.name,
        departmentCount: foundDepartments.size,
        departments: Array.from(foundDepartments),
        result: result,
        isMultiple: foundDepartments.size > 1
      });
      return result;
    }

    // Final fallback: Use a default department if available
    if (departments.length > 0) {
      console.log(`„ Using first available department as fallback:`, departments[0].name);
      return departments[0].name;
    }

    console.log(`š No Department Found - Final Debug:`, {
      employeeName: employee?.teamMemberName || employee?.name || 'Unknown',
      employeeId: employee?._id,
      employeeDepartment: employee?.department,
      employeeDepartmentId: employee?.departmentId,
      employeeDepartmentName: employee?.departmentName,
      employeeRole: employee?.role,
      employeePosition: employee?.position,
      projectsChecked: localProjects.length,
      availableDepartments: departments.length,
      departmentsList: departments.map(d => ({ id: d._id, name: d.name })),
      taskProjectFound: !!taskProject,
      taskProjectDepartment: taskProject?.department
    });

    return 'No Department';
  };

  const getProjectNameForMilestone = (milestone) => {
    // First priority: Use projectName directly from milestone
    if (milestone.projectName) {
      return milestone.projectName;
    }

    // Second priority: Try to find project by ID
    const projectId = milestone.projectId || milestone.project;
    if (projectId && localProjects.length > 0) {
      let project = localProjects.find(p => p._id === projectId);

      if (!project) {
        project = localProjects.find(p => p._id?.toString() === projectId?.toString());
      }

      if (project) {
        return project.title || project.name;
      }
    }

    // Third priority: Check if project field is an object with name
    if (milestone.project && typeof milestone.project === 'object') {
      const result = milestone.project.title || milestone.project.name;
      if (result) {
        return result;
      }
    }

    return 'Unknown Project';
  };

  // Safely convert various possible description/history shapes to a string for rendering
  const safeText = (val) => {
    if (val == null) return '';
    if (typeof val === 'string') return val;
    if (Array.isArray(val)) return val.filter(Boolean).join('\n');
    if (typeof val === 'object') {
      // Handle array-like objects with numeric keys
      const keys = Object.keys(val);
      const numericKeys = keys.filter(k => /^\d+$/.test(k)).sort((a, b) => Number(a) - Number(b));
      if (numericKeys.length > 0) {
        return numericKeys.map(k => String(val[k] ?? '')).filter(Boolean).join('\n');
      }
      // Fallback: try common fields
      if (val.text) return String(val.text);
      if (val.description) return String(val.description);
      // Last resort: stringify safely
      try { return JSON.stringify(val); } catch (e) { return String(val); }
    }
    return String(val);
  };

  // Build a normalized list of attachments for a task combining backend files and latest client uploads
  // Build a normalized list of attachments for a task combining backend files and latest client uploads
  const buildAttachmentItemsForTask = (task) => {
    if (!task) return [];
    const sid = task._id || task.id;
    const uploadedFiles = (statusUpdates[sid]?.files) || [];

    // ✅ FIXED: Collect from specific arrays, avoiding duplicates
    const images = Array.isArray(task?.images) ? task.images : [];
    const audios = Array.isArray(task?.audios) ? task.audios : [];
    const files = Array.isArray(task?.files) ? task.files : [];

    // Combine all backend files (images, audios, files)
    const rawBackend = [...images, ...audios, ...files];

    // Use localhost if running locally
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const API_BASE = isLocalhost ? 'https://slateblue-mallard-430836.hostingersite.com/api' : 'https://slateblue-mallard-430836.hostingersite.com/api';

    const normalize = (bf) => {
      let url = '';
      let name = 'file';
      if (typeof bf === 'string') {
        url = bf;
        name = bf.split('/').pop();
      } else if (bf && typeof bf === 'object') {
        url = bf.url || bf.path || bf.location || bf.secure_url || '';
        name = bf.name || bf.filename || (url ? url.split('/').pop() : 'file');
      }
      if (url && url.startsWith('/')) {
        url = `${API_BASE}${url}`;
      }
      return { url, name };
    };

    // ✅ Remove duplicates by URL
    const backendItems = rawBackend
      .map(normalize)
      .filter(it => it.url);

    // Remove duplicate URLs
    const uniqueBackendItems = backendItems.filter((item, index, self) =>
      index === self.findIndex(t => t.url === item.url)
    );

    const uploadedItems = uploadedFiles.map(uf => ({
      url: URL.createObjectURL(uf),
      name: uf.name,
      revoke: true,
      mime: uf.type
    }));

    const items = [...uniqueBackendItems, ...uploadedItems].map(it => ({
      ...it,
      isImg: (it.mime ? it.mime.startsWith('image/') : /\.(png|jpe?g|gif|webp)$/i.test(it.url))
    }));

    return items;
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setDepartmentFilter('all');
    setEmployeeFilter('all');
  };

  // Styles
  const pageStyle = {
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    padding: '2rem',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '2rem',
    padding: '2rem',
    background: 'rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(25px)',
    borderRadius: '2rem',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
  };

  const titleStyle = {
    fontSize: '2.5rem',
    fontWeight: 900,
    color: '#1f2937',
    margin: 0,
    letterSpacing: '1px'
  };

  const gridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))",
    gap: "2rem"
  };

  const tableContainerStyle = {
    backgroundColor: 'white',
    borderRadius: '1rem',
    overflowX: 'auto',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e5e7eb',
    marginBottom: '2rem'
  };

  const thStyle = {
    padding: '1.25rem 1rem',
    backgroundColor: '#f9fafb',
    borderBottom: '2px solid #e5e7eb',
    textAlign: 'left',
    fontSize: '0.85rem',
    fontWeight: 700,
    color: '#374151',
    textTransform: 'uppercase',
    letterSpacing: '0.025em',
    whiteSpace: 'nowrap'
  };

  const tdStyle = {
    padding: '1.25rem 1rem',
    borderBottom: '1px solid #f3f4f6',
    fontSize: '0.9rem',
    color: '#4b5563',
    verticalAlign: 'middle'
  };

  const emptyStateStyle = {
    gridColumn: "1 / -1",
    textAlign: "center",
    padding: "4rem 2rem",
    backgroundColor: "white",
    borderRadius: "1rem",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)"
  };

  if (loading) {
    return (
      <div style={pageStyle}>
        {/* Removed main header from top */}
        {/* Change Task Status Modal */}
        {showStatusModal && selectedTask && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
            <div style={{ background: 'white', borderRadius: '1rem', padding: '1.25rem', width: '95%', maxWidth: 520 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h3 style={{ margin: 0 }}>Change Task Status</h3>
                <button onClick={() => setShowStatusModal(false)} style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: '0.5rem', padding: '0.35rem 0.7rem', cursor: 'pointer' }}>Close</button>
              </div>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                <div style={{ background: '#f3f4f6', padding: 8, borderRadius: 6, fontSize: '0.8rem', color: '#374151' }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Debug</div>
                  <div>milestoneId: {String(statusForm.milestoneId || '')}</div>
                  <div>Status: {String(statusForm.status || '')}</div>
                  <div style={{ marginTop: 6 }}><em>Open console for full statusForm object</em></div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Status</label>
                  <select value={statusForm.status} onChange={(e) => setStatusForm(prev => ({ ...prev, status: e.target.value }))} style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.5rem' }}>
                    <option value="">-- Select Status --</option>
                    <option value="Pending">Pending</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="Overdue">Overdue</option>
                  </select>
                </div>

                {/* Update History - Shows status changes with descriptions */}
                <div style={{ padding: '0.75rem', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>📋 Status Update History</div>
                  {(() => {
                    const hist = selectedTask?.statusHistory || [];
                    if (!Array.isArray(hist) || hist.length === 0) {
                      return <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>No status updates yet.</div>;
                    }
                    return (
                      <div style={{ display: 'grid', gap: 10 }}>
                        {hist.slice().reverse().map((entry, idx) => {
                          const when = entry.updatedAt || entry.createdAt;
                          const descriptions = Array.isArray(entry.description) ? entry.description : [];
                          const updatedBy = entry.updatedBy?.teamMemberName || entry.updatedBy?.name || 'Unknown';

                          return (
                            <div key={idx} style={{ padding: 10, border: '1px solid #e5e7eb', borderRadius: 6, background: '#f9fafb' }}>
                              {/* Status Badge and Timestamp */}
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                <span style={{
                                  ...getStatusBadgeStyle(entry.status),
                                  fontSize: '0.75rem',
                                  padding: '0.2rem 0.5rem'
                                }}>
                                  {entry.status || 'Unknown'}
                                </span>
                                <div style={{ color: '#6b7280', fontSize: '0.75rem' }}>
                                  {when ? new Date(when).toLocaleString() : ''}
                                </div>
                              </div>

                              {/* Updated By */}
                              {entry.updatedBy && (
                                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: 4 }}>
                                  Updated by: {updatedBy}
                                </div>
                              )}

                              {/* Descriptions */}
                              {descriptions.length > 0 && (
                                <div style={{ marginTop: 6 }}>
                                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                                    Notes:
                                  </div>
                                  <div style={{ color: '#4b5563', fontSize: '0.85rem', whiteSpace: 'pre-wrap', paddingLeft: '0.5rem', borderLeft: '2px solid #e5e7eb' }}>
                                    {descriptions.map((desc, i) => (
                                      <div key={i} style={{ marginBottom: 2 }}>• {desc}</div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>

                {/* Update History (full list in Task Detail) */}
                <div style={{ padding: '0.75rem', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Update History</div>
                  {(() => {
                    const hist = (selectedTask.statusHistory || selectedTask.history || selectedTask.updates || []);
                    const all = Array.isArray(hist) ? hist : [];
                    if (all.length === 0) {
                      return <div style={{ color: '#6b7280' }}>No history yet.</div>;
                    }
                    return (
                      <div style={{ display: 'grid', gap: 8 }}>
                        {all.slice().reverse().map((h, idx) => {
                          const when = h.at || h.date || h.createdAt || h.updatedAt;
                          const urls = h.attachmentUrls || h.attachments || h.files || [];
                          const urlArr = Array.isArray(urls) ? urls : [];
                          return (
                            <div key={idx} style={{ padding: 8, border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                <div><strong>{h.status || 'Update'}</strong></div>
                                <div style={{ color: '#6b7280', fontSize: '0.8rem' }}>{when ? new Date(when).toLocaleString() : ''}</div>
                              </div>
                              {h?.description && (
                                <div style={{ color: '#4b5563', whiteSpace: 'pre-wrap' }}>{safeText(h.description)}</div>
                              )}
                              {urlArr.length > 0 && (
                                <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                  {urlArr.map((u, i) => (
                                    <a key={i} href={u} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontSize: '0.85rem', wordBreak: 'break-all' }}>{u}</a>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>

                {/* Update History (full list) */}
                <div style={{ padding: '0.75rem', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Update History</div>
                  {(() => {
                    const hist = (selectedTask.statusHistory || selectedTask.history || selectedTask.updates || []);
                    const all = Array.isArray(hist) ? hist : [];
                    if (all.length === 0) {
                      return <div style={{ color: '#6b7280' }}>No history yet.</div>;
                    }
                    return (
                      <div style={{ display: 'grid', gap: 8 }}>
                        {all.slice().reverse().map((h, idx) => {
                          const when = h.at || h.date || h.createdAt || h.updatedAt;
                          const urls = h.attachmentUrls || h.attachments || h.files || [];
                          const urlArr = Array.isArray(urls) ? urls : [];
                          return (
                            <div key={idx} style={{ padding: 8, border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                <div><strong>{h.status || 'Update'}</strong></div>
                                <div style={{ color: '#6b7280', fontSize: '0.8rem' }}>{when ? new Date(when).toLocaleString() : ''}</div>
                              </div>
                              {h?.description && (
                                <div style={{ color: '#4b5563', whiteSpace: 'pre-wrap' }}>{safeText(h.description)}</div>
                              )}
                              {urlArr.length > 0 && (
                                <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                  {urlArr.map((u, i) => (
                                    <a key={i} href={u} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontSize: '0.85rem', wordBreak: 'break-all' }}>{u}</a>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Description</label>
                  <textarea rows={4} value={statusForm.description} onChange={(e) => setStatusForm(prev => ({ ...prev, description: e.target.value }))} style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', resize: 'vertical' }} placeholder="Write something about this status update..." />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Attachment (max 2 MB each)</label>
                  <input
                    type="file"
                    multiple
                    accept=".jpg,.jpeg,.png,.pdf,.docx,.txt,image/*"
                    onChange={handleStatusFileChange}
                  />
                  {statusForm.files?.length > 0 && (
                    <ul style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#374151' }}>
                      {statusForm.files.map((f, i) => (
                        <li key={i}>
                          {f.name} ({Math.round(f.size / 1024)} KB)
                        </li>
                      ))}
                    </ul>
                  )}

                  <div style={{ marginTop: 6, color: '#6b7280', fontSize: '0.8rem' }}>
                    Allowed: JPG, JPEG, PNG, PDF, DOCX, TXT. Max size 2 MB per file.
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Image URL</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      type="url"
                      placeholder="https://...image.jpg"
                      value={statusForm.urlInput}
                      onChange={(e) => setStatusForm(prev => ({ ...prev, urlInput: e.target.value }))}
                      style={{ flex: 1, padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.5rem' }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const u = (statusForm.urlInput || '').trim();
                        if (!u) return;
                        setStatusForm(prev => ({ ...prev, urlList: [...prev.urlList, u], urlInput: '' }));
                      }}
                      style={{ padding: '0.5rem 0.9rem', borderRadius: '0.5rem', background: '#10b981', color: '#fff', border: 'none', cursor: 'pointer' }}
                    >
                      Add URL
                    </button>
                  </div>
                  {statusForm.urlList.length > 0 && (
                    <ul style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#374151' }}>
                      {statusForm.urlList.map((u, i) => (<li key={i} style={{ wordBreak: 'break-all' }}>{u}</li>))}
                    </ul>
                  )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                  <button type="button" onClick={() => setShowStatusModal(false)} style={{ padding: '0.5rem 0.9rem', borderRadius: '0.5rem', border: '1px solid #e5e7eb', background: 'white', cursor: 'pointer' }}>Cancel</button>
                  <button
                    type="button"
                    onClick={async () => {
                      console.log('Update button clicked (modal - loading branch)', { uploadingMedia, statusForm });
                      try {
                        setUploadingMedia(true);
                        await withTimeout(submitTaskStatusChange(), 25000);
                      } catch (err) {
                        console.error('Error in Update handler (loading branch):', err);
                        if (err && err.message === 'timeout') {
                          setShowAlert('Update timed out. Please check your network and try again.');
                        } else {
                          setShowAlert('Failed to update task. See console for details.');
                        }
                      } finally {
                        setUploadingMedia(false);
                      }
                    }}
                    disabled={uploadingMedia}
                    style={{ padding: '0.5rem 0.9rem', borderRadius: '0.5rem', background: uploadingMedia ? '#93c5fd' : '#2563eb', color: 'white', border: 'none', cursor: uploadingMedia ? 'not-allowed' : 'pointer' }}
                  >
                    {uploadingMedia ? 'Updating...' : 'Update'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        <div style={{ textAlign: 'center', padding: '4rem' }}>
          <div style={{ fontSize: '1.2rem', color: '#6b7280' }}>Loading project-tasks...</div>
        </div>
      </div>
    );
  }

  // Filter followups based on search query, status, department, and employee
  const filteredFollowups = allFollowups.filter((followup, index) => {
    // Defensive: skip null/invalid entries
    if (!followup || typeof followup !== 'object') return false;
    // Apply search filter
    let matchesSearch = true;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      const title = (typeof followup.title === 'string' ? followup.title : (followup.title || '')).toLowerCase();
      const description = (Array.isArray(followup.description) ? followup.description.join(' ') : (typeof followup.description === 'string' ? followup.description : (followup.description || ''))).toLowerCase();
      const projectName = (typeof (followup.projectName || getProjectNameForMilestone(followup)) === 'string' ? (followup.projectName || getProjectNameForMilestone(followup)) : String(followup.projectName || getProjectNameForMilestone(followup))).toLowerCase();
      const assignedMember = getEmployeeName(followup.assignedTeamMember).toLowerCase();
      const status = calculateDynamicStatus(followup).toLowerCase();
      // Get department from project (matching display and filter logic)
      const projectForTask = localProjects.find(p =>
        (p.title === followup.projectName) ||
        (p._id === followup.projectId) ||
        (p.title === getProjectNameForMilestone(followup))
      );

      let department = 'no department';
      if (projectForTask && projectForTask.department) {
        if (Array.isArray(projectForTask.department)) {
          const deptNames = projectForTask.department.map(dept => {
            if (typeof dept === 'object' && dept.name) return dept.name;
            if (typeof dept === 'string') {
              const foundDept = departments.find(d => d._id === dept || d.id === dept);
              return foundDept ? (foundDept.name || foundDept.departmentName) : dept;
            }
            return 'unknown';
          });
          department = deptNames.join(' ').toLowerCase();
        } else {
          if (typeof projectForTask.department === 'object' && projectForTask.department.name) {
            department = projectForTask.department.name.toLowerCase();
          } else if (typeof projectForTask.department === 'string') {
            const foundDept = departments.find(d => d._id === projectForTask.department || d.id === projectForTask.department);
            department = foundDept ? (foundDept.name || foundDept.departmentName || projectForTask.department).toLowerCase() : projectForTask.department.toLowerCase();
          }
        }
      }

      matchesSearch = title.includes(query) ||
        description.includes(query) ||
        projectName.includes(query) ||
        assignedMember.includes(query) ||
        status.includes(query) ||
        department.includes(query);
    }

    // Apply status filter using dynamic status calculation
    let matchesStatus = true;
    if (statusFilter !== 'all') {
      matchesStatus = calculateDynamicStatus(followup) === statusFilter;
    }

    // Apply department filter using project-based department logic (matching display logic)
    let matchesDepartment = true;
    if (departmentFilter !== 'all') {
      // Find the project this task belongs to (same logic as display)
      const projectForTask = localProjects.find(p =>
        (p.title === followup.projectName) ||
        (p._id === followup.projectId) ||
        (p.title === getProjectNameForMilestone(followup))
      );

      if (!projectForTask || !projectForTask.department) {
        matchesDepartment = false;
        if (index < 3) {
          console.log(`No Project/Department Found [Task ${index + 1}]:`, {
            taskTitle: followup.title,
            projectFound: !!projectForTask,
            selectedFilter: departmentFilter,
            matches: false
          });
        }
      } else {
        // Handle project department (matching display logic)
        if (Array.isArray(projectForTask.department)) {
          // Multiple departments - check if any match
          matchesDepartment = projectForTask.department.some(dept => {
            let deptName = 'Unknown Department';
            if (typeof dept === 'object' && dept.name) {
              deptName = dept.name;
            } else if (typeof dept === 'string') {
              const foundDept = departments.find(d => d._id === dept || d.id === dept);
              deptName = foundDept ? (foundDept.name || foundDept.departmentName || 'Unknown Department') : dept;
            }

            const exactMatch = deptName === departmentFilter;
            const trimmedMatch = deptName.trim() === departmentFilter.trim();
            const caseInsensitiveMatch = deptName.toLowerCase().trim() === departmentFilter.toLowerCase().trim();

            return exactMatch || trimmedMatch || caseInsensitiveMatch;
          });
        } else {
          // Single department
          let deptName = 'Unknown Department';
          if (typeof projectForTask.department === 'object' && projectForTask.department.name) {
            deptName = projectForTask.department.name;
          } else if (typeof projectForTask.department === 'string') {
            const foundDept = departments.find(d => d._id === projectForTask.department || d.id === projectForTask.department);
            deptName = foundDept ? (foundDept.name || foundDept.departmentName || 'Unknown Department') : projectForTask.department;
          }

          const exactMatch = deptName === departmentFilter;
          const trimmedMatch = deptName.trim() === departmentFilter.trim();
          const caseInsensitiveMatch = deptName.toLowerCase().trim() === departmentFilter.toLowerCase().trim();

          matchesDepartment = exactMatch || trimmedMatch || caseInsensitiveMatch;
        }

        // Debug logging for project-based filtering
        if (index < 5) {
          console.log(`Project Department Filter Debug [Task ${index + 1}]:`, {
            taskTitle: followup.title,
            projectTitle: projectForTask.title,
            projectDepartment: projectForTask.department,
            selectedFilter: departmentFilter,
            matches: matchesDepartment,
            isArray: Array.isArray(projectForTask.department)
          });
        }
      }
    }

    // Apply employee filter (handle both single and multiple employees)
    let matchesEmployee = true;
    if (employeeFilter !== 'all') {
      // If there's no assigned team member, it doesn't match a specific employee
      if (!followup.assignedTeamMember) {
        matchesEmployee = false;
      }
      // Handle multiple assigned team members (array)
      else if (Array.isArray(followup.assignedTeamMember)) {
        matchesEmployee = followup.assignedTeamMember.some(memberId => {
          if (!memberId) return false;
          if (typeof memberId === 'object') {
            return (memberId._id || memberId.id) === employeeFilter;
          }
          return String(memberId) === String(employeeFilter);
        });
      } else {
        // Handle single assigned team member
        if (typeof followup.assignedTeamMember === 'object') {
          matchesEmployee = (followup.assignedTeamMember._id || followup.assignedTeamMember.id) === employeeFilter;
        } else {
          matchesEmployee = String(followup.assignedTeamMember) === String(employeeFilter);
        }
      }

      // Debug logging for employee filter
      if (index < 3) {
        console.log(`Employee Filter Debug [Task ${index + 1}]:`, {
          taskTitle: followup.title,
          assignedTeamMember: followup.assignedTeamMember,
          isArray: Array.isArray(followup.assignedTeamMember),
          selectedEmployeeFilter: employeeFilter,
          matches: matchesEmployee
        });
      }
    }

    // Debug logging for combined filters
    if (index < 3 && (departmentFilter !== 'all' || employeeFilter !== 'all' || statusFilter !== 'all')) {
      console.log(`„ Combined Filter Debug [Task ${index + 1}]:`, {
        taskTitle: followup.title,
        filters: {
          search: searchQuery ? `"${searchQuery}"` : 'none',
          department: departmentFilter,
          employee: employeeFilter !== 'all' ? employees.find(e => e._id === employeeFilter)?.teamMemberName || employeeFilter : 'all',
          status: statusFilter
        },
        matches: {
          search: matchesSearch,
          status: matchesStatus,
          department: matchesDepartment,
          employee: matchesEmployee,
          final: matchesSearch && matchesStatus && matchesDepartment && matchesEmployee
        }
      });
    }

    return matchesSearch && matchesStatus && matchesDepartment && matchesEmployee;
  });

  // Comprehensive debug logging for department filtering results (using project-based logic)
  if (departmentFilter !== 'all') {
    const allTaskDepartments = allFollowups.map(f => {
      const projectForTask = localProjects.find(p =>
        (p.title === f.projectName) ||
        (p._id === f.projectId) ||
        (p.title === getProjectNameForMilestone(f))
      );

      let projectDepartments = 'No Department';
      if (projectForTask && projectForTask.department) {
        if (Array.isArray(projectForTask.department)) {
          const deptNames = projectForTask.department.map(dept => {
            if (typeof dept === 'object' && dept.name) return dept.name;
            if (typeof dept === 'string') {
              const foundDept = departments.find(d => d._id === dept || d.id === dept);
              return foundDept ? (foundDept.name || foundDept.departmentName) : dept;
            }
            return 'Unknown';
          });
          projectDepartments = deptNames.join(', ');
        } else {
          if (typeof projectForTask.department === 'object' && projectForTask.department.name) {
            projectDepartments = projectForTask.department.name;
          } else if (typeof projectForTask.department === 'string') {
            const foundDept = departments.find(d => d._id === projectForTask.department || d.id === projectForTask.department);
            projectDepartments = foundDept ? (foundDept.name || foundDept.departmentName) : projectForTask.department;
          }
        }
      }

      return {
        title: f.title,
        departments: projectDepartments,
        projectTitle: projectForTask?.title || 'No Project',
        employee: getEmployeeById(f.assignedTeamMember)?.name || 'Unknown'
      };
    });

    const tasksWithMultipleDepts = allTaskDepartments.filter(task =>
      task.departments.includes(',')
    );

    const shouldMatchTasks = allTaskDepartments.filter(task => {
      if (task.departments === 'No Department') return false;
      const taskDepts = task.departments.split(',').map(d => d.trim());
      return taskDepts.some(dept =>
        dept === departmentFilter ||
        dept.trim() === departmentFilter.trim() ||
        dept.toLowerCase().trim() === departmentFilter.toLowerCase().trim()
      );
    });

    console.log(`COMPREHENSIVE Department Filter Analysis:`, {
      selectedDepartment: departmentFilter,
      totalTasks: allFollowups.length,
      actualFilteredTasks: filteredFollowups.length,
      shouldMatchTasks: shouldMatchTasks.length,
      tasksWithMultipleDepartments: tasksWithMultipleDepts.length,
      availableDepartments: departments.map(d => d.name),
      allTaskDepartments: allTaskDepartments,
      tasksThatShouldMatch: shouldMatchTasks.map(task => ({
        title: task.title,
        departments: task.departments,
        projectTitle: task.projectTitle,
        departmentList: task.departments.split(',').map(d => d.trim())
      })),
      // Special check for Field Sales
      tasksWithFieldSales: allTaskDepartments.filter(task => {
        return task.departments.toLowerCase().includes('field');
      }).map(task => ({
        title: task.title,
        departments: task.departments,
        projectTitle: task.projectTitle
      }))
    });

    // Check if there's a mismatch for department filtering
    if (shouldMatchTasks.length !== filteredFollowups.length) {
      console.warn(`⚠️ DEPARTMENT FILTERING MISMATCH DETECTED:`, {
        expected: shouldMatchTasks.length,
        actual: filteredFollowups.length,
        difference: shouldMatchTasks.length - filteredFollowups.length
      });
    }
  }

  // Comprehensive debug logging for employee filtering results
  if (employeeFilter !== 'all') {
    const allTaskEmployees = allFollowups.map(f => ({
      title: f.title,
      assignedTeamMember: f.assignedTeamMember,
      isArray: Array.isArray(f.assignedTeamMember),
      employeeNames: Array.isArray(f.assignedTeamMember)
        ? f.assignedTeamMember.map(memberId => {
          if (memberId && typeof memberId === 'object') {
            return memberId.teamMemberName || memberId.name || 'Unknown';
          }
          const emp = employees.find(e => e._id === memberId);
          return emp ? (emp.teamMemberName || emp.name || 'Unknown') : 'Unknown Employee';
        })
        : [(() => {
          if (f.assignedTeamMember && typeof f.assignedTeamMember === 'object') {
            return f.assignedTeamMember.teamMemberName || f.assignedTeamMember.name || 'Unknown';
          }
          const emp = employees.find(e => e._id === f.assignedTeamMember);
          return emp ? (emp.teamMemberName || emp.name || 'Unknown') : 'Unknown Employee';
        })()]
    }));

    const selectedEmployeeName = employees.find(emp => emp._id === employeeFilter)?.teamMemberName ||
      employees.find(emp => emp._id === employeeFilter)?.name || 'Unknown';

    console.log(`COMPREHENSIVE Employee Filter Analysis:`, {
      selectedEmployee: employeeFilter,
      selectedEmployeeName: selectedEmployeeName,
      totalTasks: allFollowups.length,
      actualFilteredTasks: filteredFollowups.length,
      availableEmployees: employees.map(e => ({ id: e._id, name: e.teamMemberName || e.name })),
      allTaskEmployees: allTaskEmployees
    });
  }

  // Overall filter summary when multiple filters are active
  if (departmentFilter !== 'all' || employeeFilter !== 'all' || statusFilter !== 'all' || searchQuery.trim()) {
    console.log(`📊 OVERALL FILTER SUMMARY:`, {
      activeFilters: {
        search: searchQuery ? `"${searchQuery}"` : 'none',
        department: departmentFilter,
        employee: employeeFilter !== 'all' ? employees.find(e => e._id === employeeFilter)?.teamMemberName || employeeFilter : 'all',
        status: statusFilter
      },
      results: {
        totalTasks: allFollowups.length,
        filteredTasks: filteredFollowups.length,
        filteringRate: `${((filteredFollowups.length / allFollowups.length) * 100).toFixed(1)}%`
      },
      filtersActive: {
        searchActive: !!searchQuery.trim(),
        departmentActive: departmentFilter !== 'all',
        employeeActive: employeeFilter !== 'all',
        statusActive: statusFilter !== 'all'
      }
    });
  }

  const mobileStyles = `
    .fut-desktop-only {
      display: flex !important;
    }
    .fut-mobile-only {
      display: none !important;
    }
    
    @keyframes futDropSlide {
      from { opacity: 0; transform: translateY(-8px) scale(0.98); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }

    .fut-mobile-dropdown-menu {
      position: absolute;
      top: calc(100% + 6px);
      left: 0;
      right: 0;
      background: rgba(255, 255, 255, 0.98);
      backdrop-filter: blur(16px);
      -webkit-backdrop-filter: blur(16px);
      border: 1px solid rgba(59, 130, 246, 0.15);
      border-radius: 0.75rem;
      box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 16px -6px rgba(0, 0, 0, 0.05);
      z-index: 1000;
      overflow: hidden;
      max-height: 220px;
      overflow-y: auto;
      animation: futDropSlide 0.18s cubic-bezier(0.16, 1, 0.3, 1) both;
    }

    .fut-mobile-dropdown-item {
      display: flex;
      align-items: center;
      gap: 0.625rem;
      padding: 0.65rem 0.85rem;
      font-size: 0.875rem;
      color: #374151;
      cursor: pointer;
      transition: all 0.15s ease;
      border-bottom: 1px solid #f3f4f6;
      min-height: 40px;
      box-sizing: border-box;
    }

    .fut-mobile-dropdown-item:last-child {
      border-bottom: none;
    }

    .fut-mobile-dropdown-item:active {
      background-color: #eff6ff;
    }

    .fut-mobile-dropdown-item.fut-active {
      background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
      color: #1d4ed8;
      font-weight: 700;
      box-shadow: inset 3px 0 0 #3b82f6;
    }

    .fut-mobile-dropdown-item .fut-check {
      width: 16px;
      height: 16px;
      border-radius: 50%;
      border: 2px solid #d1d5db;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.15s ease;
    }

    .fut-mobile-dropdown-item.fut-active .fut-check {
      border-color: #3b82f6;
      background: #3b82f6;
    }

    @media (max-width: 768px) {
      .fut-desktop-only {
        display: none !important;
      }
      .fut-mobile-only {
        display: flex !important;
      }
      .fut-tasks-container {
        background: #f8fafc !important;
        padding: 1rem !important;
      }
      .fut-table-container {
        background: transparent !important;
        box-shadow: none !important;
        border: none !important;
        overflow-x: visible !important;
        padding: 0 !important;
      }
      .fut-table {
        min-width: 100% !important;
        display: block !important;
      }
      .fut-table thead {
        display: none !important;
      }
      .fut-table tbody {
        display: block !important;
      }
      .fut-table tr {
        display: flex !important;
        flex-direction: column !important;
        margin-bottom: 1rem !important;
        background: white !important;
        border-radius: 1rem !important;
        padding: 1rem !important;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1) !important;
        border: 1px solid #e5e7eb !important;
      }
      .fut-table td {
        display: flex !important;
        justify-content: space-between !important;
        align-items: center !important;
        padding: 0.75rem 0 !important;
        border-bottom: 1px solid #f3f4f6 !important;
      }
      .fut-table td:last-child {
        border-bottom: none !important;
        padding-bottom: 0 !important;
        margin-top: 0.5rem !important;
        justify-content: flex-end !important;
      }
      .fut-table td::before {
        content: attr(data-label);
        font-size: 0.75rem;
        font-weight: 700;
        color: #6b7280;
        text-transform: uppercase;
        margin-right: 1rem;
      }
      .fut-table td > div, .fut-table td > span {
        text-align: right !important;
      }
      .fut-table td:first-child {
        flex-direction: column !important;
        align-items: flex-start !important;
        padding-bottom: 0.75rem !important;
      }
      .fut-table td:first-child::before {
        display: none !important;
      }
      .fut-table td:first-child > div {
        text-align: left !important;
      }
      .fut-table td[colSpan="6"] {
        display: block !important;
        text-align: center !important;
        border: none !important;
      }
      .fut-table td[colSpan="6"]::before {
        display: none !important;
      }
      .fut-mobile-filters-box {
        background: linear-gradient(135deg, #0c1a4e 0%, #1a3a8f 35%, #1e56c8 70%, #0c1a4e 100%) !important;
        box-shadow: 0 0 40px rgba(59,130,246,0.25), 0 0 80px rgba(37,99,235,0.12), 0 8px 32px rgba(0,0,0,0.4) !important;
        border: 1px solid rgba(96,165,250,0.3) !important;
        border-radius: 1rem !important;
        padding: 1rem !important;
      }
      .fut-mobile-search-bar {
        background: rgba(255, 255, 255, 0.1) !important;
        border: 1px solid rgba(96, 165, 250, 0.4) !important;
        box-shadow: 0 0 10px rgba(59, 130, 246, 0.15) !important;
      }
      .fut-mobile-search-bar input {
        color: #ffffff !important;
      }
      .fut-mobile-search-bar input::placeholder {
        color: rgba(255, 255, 255, 0.5) !important;
      }
      .fut-mobile-search-bar svg {
        color: #60a5fa !important;
      }
      .fut-mobile-trigger {
        background-color: rgba(255, 255, 255, 0.1) !important;
        border: 1px solid rgba(96, 165, 250, 0.4) !important;
        color: #ffffff !important;
      }
      .fut-mobile-trigger span {
        color: #ffffff !important;
      }
      .fut-mobile-trigger svg {
        color: rgba(255, 255, 255, 0.6) !important;
      }
      .fut-mobile-trigger.fut-open {
        box-shadow: 0 0 15px rgba(59, 130, 246, 0.5), 0 2px 8px rgba(59, 130, 246, 0.15) !important;
        border-color: rgba(96, 165, 250, 0.8) !important;
      }
      .fut-mobile-dropdown-menu {
        background: #0c1a4e !important;
        border: 1px solid rgba(96, 165, 250, 0.4) !important;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5) !important;
      }
      .fut-mobile-dropdown-item {
        color: rgba(255, 255, 255, 0.85) !important;
        border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
      }
      .fut-mobile-dropdown-item:active {
        background-color: rgba(59, 130, 246, 0.2) !important;
      }
      .fut-mobile-dropdown-item.fut-active {
        background: rgba(59, 130, 246, 0.3) !important;
        color: #ffffff !important;
        box-shadow: inset 3px 0 0 #3b82f6 !important;
      }
      .fut-mobile-dropdown-item .fut-check {
        border-color: rgba(255, 255, 255, 0.3) !important;
      }
      .fut-mobile-dropdown-item.fut-active .fut-check {
        border-color: #3b82f6 !important;
        background: #3b82f6 !important;
      }
    }
  `;

  return (
    <div className="fut-tasks-container" style={pageStyle}>
      <style dangerouslySetInnerHTML={{ __html: mobileStyles }} />

      {/* Overlay to close custom mobile dropdowns when tapping outside */}
      {activeMobileDropdown && (
        <div
          onClick={() => setActiveMobileDropdown(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999,
            background: 'transparent'
          }}
        />
      )}

      {/* Desktop Search and Filter Bar */}
      <div className="fut-desktop-only" style={{
        marginBottom: '2rem',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '1rem',
        flexWrap: 'wrap'
      }}>


        {/* Search Bar */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          backgroundColor: 'white',
          padding: '0.75rem 1.5rem',
          borderRadius: '1rem',
          border: '2px solid #10b981',
          boxShadow: '0 4px 15px rgba(16, 185, 129, 0.2)',
          minWidth: '350px',
          maxWidth: '500px',
          flex: 1
        }}>
          <Search size={20} style={{ color: '#10b981' }} />
          <input
            type="text"
            placeholder="Search by title, description, project, assignee, department..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#1f2937',
              fontWeight: 500,
              fontSize: '0.95rem',
              outline: 'none',
              width: '100%',
              '::placeholder': {
                color: '#6b7280'
              }
            }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{
                background: 'none',
                border: 'none',
                color: '#6b7280',
                cursor: 'pointer',
                padding: '0.25rem',
                borderRadius: '0.25rem',
                fontSize: '1.2rem',
                lineHeight: 1
              }}
              title="Clear search"
            >
              ×
            </button>
          )}
        </div>

        {/* Department Filter */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          backgroundColor: 'white',
          padding: '0.75rem 1rem',
          borderRadius: '1rem',
          border: '2px solid #8b5cf6',
          boxShadow: '0 4px 15px rgba(139, 92, 246, 0.2)',
          minWidth: '180px'
        }}>
          <Building2 size={20} style={{ color: '#8b5cf6' }} />
          <select
            value={departmentFilter}
            onChange={(e) => {
              const selectedValue = e.target.value;
              console.log(`🎯 Department Filter Selected:`, {
                selectedValue: `"${selectedValue}"`,
                allOptions: departments.map(d => `"${d.name}"`),
                hasFieldSales: departments.some(d => d.name.includes('Field'))
              });
              setDepartmentFilter(selectedValue);
              // Reset employee and status filters when department changes
              setEmployeeFilter('all');
              setStatusFilter('all');
            }}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#1f2937',
              fontWeight: 600,
              fontSize: '0.95rem',
              cursor: 'pointer',
              outline: 'none',
              width: '100%'
            }}
          >
            <option value="all">All Departments</option>
            {departments.map(dept => (
              <option key={dept._id || dept.id || JSON.stringify(dept)} value={(dept && dept.name) || String(dept || '')}>{(dept && dept.name) || String(dept || '')}</option>
            ))}
          </select>
        </div>

        {/* Employee Filter */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          backgroundColor: 'white',
          padding: '0.75rem 1rem',
          borderRadius: '1rem',
          border: '2px solid #3b82f6',
          boxShadow: '0 4px 15px rgba(59, 130, 246, 0.2)',
          minWidth: '180px'
        }}>
          <Users size={20} style={{ color: '#3b82f6' }} />
          <select
            value={employeeFilter}
            onChange={(e) => {
              const selectedValue = e.target.value;
              const filteredEmployees = getFilteredEmployees();
              console.log(`Employee Filter Selected:`, {
                selectedValue: selectedValue,
                selectedEmployeeName: selectedValue === 'all' ?
                  (departmentFilter === 'all' ? 'All Employees' : `All Employees in ${departmentFilter}`) :
                  filteredEmployees.find(emp => emp._id === selectedValue)?.teamMemberName ||
                  filteredEmployees.find(emp => emp._id === selectedValue)?.name || 'Unknown',
                availableEmployeesInDepartment: filteredEmployees.length,
                departmentFilter: departmentFilter
              });
              setEmployeeFilter(selectedValue);
            }}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#1f2937',
              fontWeight: 600,
              fontSize: '0.95rem',
              cursor: 'pointer',
              outline: 'none',
              width: '100%'
            }}
          >
            <option value="all">
              {departmentFilter === 'all' ? 'All Employees' : `All Employees in ${departmentFilter}`}
            </option>
            {getFilteredEmployees().map(emp => (
              <option key={emp._id} value={emp._id}>
                {emp.teamMemberName || emp.name || 'Unknown'}
              </option>
            ))}
          </select>
        </div>

        {/* Status Filter */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          backgroundColor: 'white',
          padding: '0.75rem 1rem',
          borderRadius: '1rem',
          border: '2px solid #3b82f6',
          boxShadow: '0 4px 15px rgba(59, 130, 246, 0.2)',
          minWidth: '160px'
        }}>
          <Filter size={20} style={{ color: '#3b82f6' }} />
          <select
            value={statusFilter}
            onChange={(e) => {
              const selectedValue = e.target.value;
              const filteredStatuses = getFilteredStatuses();
              console.log(`📊 Status Filter Selected:`, {
                selectedValue: selectedValue,
                availableStatusesInDepartment: filteredStatuses.length,
                statusList: filteredStatuses,
                departmentFilter: departmentFilter
              });
              setStatusFilter(selectedValue);
            }}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#1f2937',
              fontWeight: 600,
              fontSize: '0.95rem',
              cursor: 'pointer',
              outline: 'none',
              width: '100%'
            }}
          >
            <option value="all">
              {departmentFilter === 'all' ? 'All Status' : `All Status in ${departmentFilter}`}
            </option>
            {getFilteredStatuses().map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Mobile Search and Filter Bar */}
      <div className="fut-mobile-only fut-mobile-filters-box" style={{
        flexDirection: 'column',
        gap: '0.75rem',
        width: '100%',
        marginBottom: '1.5rem',
        padding: '0 0.5rem',
        boxSizing: 'border-box'
      }}>
        {/* Mobile Header: Reset Button */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%', marginBottom: '0.25rem' }}>
          {(searchQuery || departmentFilter !== 'all' || employeeFilter !== 'all' || statusFilter !== 'all') && (
            <button
              onClick={clearAllFilters}
              style={{
                background: 'none',
                border: 'none',
                color: '#3b82f6',
                fontWeight: 600,
                fontSize: '0.85rem',
                cursor: 'pointer',
                padding: '0.25rem 0.5rem'
              }}
            >
              Reset Filters
            </button>
          )}
        </div>

        {/* Search Bar */}
        <div className="fut-mobile-search-bar" style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          backgroundColor: 'white',
          padding: '0.625rem 1rem',
          borderRadius: '0.75rem',
          border: '1px solid #10b981',
          boxShadow: '0 2px 8px rgba(16, 185, 129, 0.08)',
          width: '100%',
          boxSizing: 'border-box',
          minHeight: '42px'
        }}>
          <Search size={18} style={{ color: '#10b981', flexShrink: 0 }} />
          <input
            type="text"
            placeholder="Search tasks, project, assignee..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#1f2937',
              fontWeight: 500,
              fontSize: '0.875rem',
              outline: 'none',
              width: '100%'
            }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{
                background: 'none',
                border: 'none',
                color: '#6b7280',
                cursor: 'pointer',
                fontSize: '1.1rem',
                padding: '0 0.25rem',
                lineHeight: 1
              }}
            >
              ×
            </button>
          )}
        </div>

        {/* Mobile Filters Stack */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem', width: '100%' }}>

          {/* Department Filter Custom Dropdown */}
          <div style={{ position: 'relative', width: '100%' }}>
            <div
              className={`fut-mobile-trigger ${activeMobileDropdown === 'department' ? 'fut-open' : ''}`}
              onClick={() => setActiveMobileDropdown(activeMobileDropdown === 'department' ? null : 'department')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                backgroundColor: 'white',
                padding: '0.625rem 1rem',
                borderRadius: '0.75rem',
                border: '1px solid rgba(139, 92, 246, 0.4)',
                boxShadow: activeMobileDropdown === 'department'
                  ? '0 0 12px rgba(59, 130, 246, 0.35), 0 2px 8px rgba(59, 130, 246, 0.15)'
                  : '0 2px 6px rgba(0, 0, 0, 0.03)',
                width: '100%',
                boxSizing: 'border-box',
                cursor: 'pointer',
                minHeight: '42px',
                transition: 'all 0.2s ease'
              }}
            >
              <Building2 size={18} style={{ color: '#8b5cf6', flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: '0.875rem', fontWeight: 600, color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {departmentFilter === 'all' ? 'All Departments' : departmentFilter}
              </span>
              <svg
                style={{
                  transition: 'transform 0.2s ease',
                  transform: activeMobileDropdown === 'department' ? 'rotate(180deg)' : 'rotate(0deg)',
                  color: '#6b7280',
                  flexShrink: 0
                }}
                width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </div>
            {activeMobileDropdown === 'department' && (
              <div className="fut-mobile-dropdown-menu">
                <div
                  className={`fut-mobile-dropdown-item ${departmentFilter === 'all' ? 'fut-active' : ''}`}
                  onClick={() => {
                    setDepartmentFilter('all');
                    setEmployeeFilter('all');
                    setStatusFilter('all');
                    setActiveMobileDropdown(null);
                  }}
                >
                  <span className="fut-check">
                    {departmentFilter === 'all' && (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                    )}
                  </span>
                  <span>All Departments</span>
                </div>
                {departments.map(dept => {
                  const val = (dept && dept.name) || String(dept || '');
                  return (
                    <div
                      key={dept._id || dept.id || val}
                      className={`fut-mobile-dropdown-item ${departmentFilter === val ? 'fut-active' : ''}`}
                      onClick={() => {
                        setDepartmentFilter(val);
                        setEmployeeFilter('all');
                        setStatusFilter('all');
                        setActiveMobileDropdown(null);
                      }}
                    >
                      <span className="fut-check">
                        {departmentFilter === val && (
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                        )}
                      </span>
                      <span>{val}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Employee Filter Custom Dropdown */}
          <div style={{ position: 'relative', width: '100%' }}>
            <div
              className={`fut-mobile-trigger ${activeMobileDropdown === 'employee' ? 'fut-open' : ''}`}
              onClick={() => setActiveMobileDropdown(activeMobileDropdown === 'employee' ? null : 'employee')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                backgroundColor: 'white',
                padding: '0.625rem 1rem',
                borderRadius: '0.75rem',
                border: '1px solid rgba(59, 130, 246, 0.4)',
                boxShadow: activeMobileDropdown === 'employee'
                  ? '0 0 12px rgba(59, 130, 246, 0.35), 0 2px 8px rgba(59, 130, 246, 0.15)'
                  : '0 2px 6px rgba(0, 0, 0, 0.03)',
                width: '100%',
                boxSizing: 'border-box',
                cursor: 'pointer',
                minHeight: '42px',
                transition: 'all 0.2s ease'
              }}
            >
              <Users size={18} style={{ color: '#3b82f6', flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: '0.875rem', fontWeight: 600, color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {(() => {
                  if (employeeFilter === 'all') {
                    return departmentFilter === 'all' ? 'All Employees' : `All Employees in ${departmentFilter}`;
                  }
                  const filteredEmployees = getFilteredEmployees();
                  const found = filteredEmployees.find(emp => emp._id === employeeFilter);
                  return found ? (found.teamMemberName || found.name) : 'Selected Employee';
                })()}
              </span>
              <svg
                style={{
                  transition: 'transform 0.2s ease',
                  transform: activeMobileDropdown === 'employee' ? 'rotate(180deg)' : 'rotate(0deg)',
                  color: '#6b7280',
                  flexShrink: 0
                }}
                width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </div>
            {activeMobileDropdown === 'employee' && (
              <div className="fut-mobile-dropdown-menu">
                <div
                  className={`fut-mobile-dropdown-item ${employeeFilter === 'all' ? 'fut-active' : ''}`}
                  onClick={() => {
                    setEmployeeFilter('all');
                    setActiveMobileDropdown(null);
                  }}
                >
                  <span className="fut-check">
                    {employeeFilter === 'all' && (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                    )}
                  </span>
                  <span>{departmentFilter === 'all' ? 'All Employees' : `All Employees in ${departmentFilter}`}</span>
                </div>
                {getFilteredEmployees().map(emp => (
                  <div
                    key={emp._id}
                    className={`fut-mobile-dropdown-item ${employeeFilter === emp._id ? 'fut-active' : ''}`}
                    onClick={() => {
                      setEmployeeFilter(emp._id);
                      setActiveMobileDropdown(null);
                    }}
                  >
                    <span className="fut-check">
                      {employeeFilter === emp._id && (
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                      )}
                    </span>
                    <span>{emp.teamMemberName || emp.name || 'Unknown'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Status Filter Custom Dropdown */}
          <div style={{ position: 'relative', width: '100%' }}>
            <div
              className={`fut-mobile-trigger ${activeMobileDropdown === 'status' ? 'fut-open' : ''}`}
              onClick={() => setActiveMobileDropdown(activeMobileDropdown === 'status' ? null : 'status')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                backgroundColor: 'white',
                padding: '0.625rem 1rem',
                borderRadius: '0.75rem',
                border: '1px solid rgba(59, 130, 246, 0.4)',
                boxShadow: activeMobileDropdown === 'status'
                  ? '0 0 12px rgba(59, 130, 246, 0.35), 0 2px 8px rgba(59, 130, 246, 0.15)'
                  : '0 2px 6px rgba(0, 0, 0, 0.03)',
                width: '100%',
                boxSizing: 'border-box',
                cursor: 'pointer',
                minHeight: '42px',
                transition: 'all 0.2s ease'
              }}
            >
              <Filter size={18} style={{ color: '#3b82f6', flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: '0.875rem', fontWeight: 600, color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {statusFilter === 'all' ? (departmentFilter === 'all' ? 'All Status' : `All Status in ${departmentFilter}`) : statusFilter}
              </span>
              <svg
                style={{
                  transition: 'transform 0.2s ease',
                  transform: activeMobileDropdown === 'status' ? 'rotate(180deg)' : 'rotate(0deg)',
                  color: '#6b7280',
                  flexShrink: 0
                }}
                width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </div>
            {activeMobileDropdown === 'status' && (
              <div className="fut-mobile-dropdown-menu">
                <div
                  className={`fut-mobile-dropdown-item ${statusFilter === 'all' ? 'fut-active' : ''}`}
                  onClick={() => {
                    setStatusFilter('all');
                    setActiveMobileDropdown(null);
                  }}
                >
                  <span className="fut-check">
                    {statusFilter === 'all' && (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                    )}
                  </span>
                  <span>{departmentFilter === 'all' ? 'All Status' : `All Status in ${departmentFilter}`}</span>
                </div>
                {getFilteredStatuses().map(status => (
                  <div
                    key={status}
                    className={`fut-mobile-dropdown-item ${statusFilter === status ? 'fut-active' : ''}`}
                    onClick={() => {
                      setStatusFilter(status);
                      setActiveMobileDropdown(null);
                    }}
                  >
                    <span className="fut-check">
                      {statusFilter === status && (
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                      )}
                    </span>
                    <span>{status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Action Buttons */}
      <div style={{
        marginBottom: '2rem',
        display: 'flex',
        justifyContent: 'center',
        gap: '1rem',
        flexWrap: 'wrap'
      }}>
        {/* Clear All Filters Button */}
        {/* <button
          onClick={clearAllFilters}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            backgroundColor: '#ef4444',
            color: 'white',
            padding: '0.75rem 1.5rem',
            borderRadius: '1rem',
            border: 'none',
            cursor: 'pointer',
            fontSize: '0.95rem',
            fontWeight: 600,
            boxShadow: '0 4px 15px rgba(239, 68, 68, 0.3)',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#dc2626';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#ef4444';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <X size={18} />
          Clear All Filters
        </button> */}

      </div>


      <div className="fut-table-container" style={tableContainerStyle}>
        <table className="fut-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1000px' }}>
          <thead>
            <tr>
              <th style={thStyle}>Task Name</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Associated Project</th>
              <th style={thStyle}>Due Date</th>
              <th style={thStyle}>Assigned To</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredFollowups.length === 0 ? (
              <tr className="fut-empty-state">
                <td colSpan="6" style={{ padding: '4rem 2rem', textAlign: 'center' }}>
                  {allFollowups.length === 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{ fontSize: "4rem", marginBottom: "1rem", opacity: 0.7 }}>📋</div>
                      <h3 style={{ fontSize: "1.5rem", color: "#1f2937", marginBottom: "0.5rem", fontWeight: 700 }}>No Overdue or Upcoming Project-Tasks</h3>
                      <p style={{ color: "#6b7280" }}>Overdue and upcoming project-tasks (next 7 days) will appear here.</p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Search size={48} style={{ opacity: 0.5, marginBottom: '1rem', color: '#6b7280' }} />
                      <h3 style={{ fontSize: "1.5rem", color: "#1f2937", marginBottom: "0.5rem", fontWeight: 700 }}>No Results Found</h3>
                      <p style={{ color: "#6b7280", marginBottom: "1rem" }}>No project-tasks found matching "{searchQuery}"</p>
                      <button onClick={() => setSearchQuery('')} style={{ padding: '0.5rem 1rem', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 500 }}>Clear Search</button>
                    </div>
                  )}
                </td>
              </tr>
            ) : (
              filteredFollowups.filter(f => f && typeof f === 'object').map((followup, index) => {
                const dueDate = new Date(followup.dueDate);
                const status = calculateDynamicStatus(followup);
                const projectName = followup.projectName || getProjectNameForMilestone(followup);
                const id = followup._id || followup.id;

                return (
                  <tr
                    key={id || index}
                    style={{ cursor: 'pointer', transition: 'background-color 0.2s' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    onClick={() => handleTaskClick(followup)}
                  >
                    <td style={tdStyle} data-label="Task Name">
                      <div style={{ fontWeight: 600, color: '#111827' }}>{followup.title || 'Untitled Task'}</div>
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>ID: {id?.substring(0, 8)}...</div>
                    </td>
                    <td style={tdStyle} data-label="Status">
                      <span style={{ ...getStatusBadgeStyle(status), padding: '0.35rem 0.75rem', fontSize: '0.75rem', borderRadius: '1rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        {status === 'Pending' && ''}
                        {status === 'In Progress' && '„'}
                        {status === 'Completed' && '✅'}
                        {status === 'Late Completed' && ''}
                        {status === 'Overdue' && '⚠️'}
                        {status}
                      </span>
                    </td>
                    <td style={tdStyle} data-label="Associated Project">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Target size={14} style={{ color: '#8b5cf6' }} />
                        <span>{projectName}</span>
                      </div>
                    </td>
                    <td style={tdStyle} data-label="Due Date">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Calendar size={14} style={{ color: '#f59e0b' }} />
                        <span>{dueDate.toLocaleDateString('en-GB')}</span>
                      </div>
                    </td>
                    <td style={tdStyle} data-label="Assigned To">
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                        {(() => {
                          if (Array.isArray(followup.assignedTeamMember)) {
                            if (followup.assignedTeamMember.length === 0) return <em style={{ color: '#9ca3af' }}>Unassigned</em>;
                            return followup.assignedTeamMember.map((memberId, idx) => {
                              const name = typeof memberId === 'object' ? (memberId.teamMemberName || memberId.name) : (employees?.find(emp => emp._id === memberId)?.teamMemberName || employees?.find(emp => emp._id === memberId)?.name);
                              return (
                                <span key={idx} style={{ backgroundColor: '#e0f2fe', color: '#0369a1', padding: '0.15rem 0.4rem', borderRadius: '0.25rem', fontSize: '0.75rem', fontWeight: 500 }}>
                                  {name || 'Unknown'}
                                </span>
                              );
                            });
                          } else {
                            const name = getEmployeeName(followup.assignedTeamMember);
                            if (!name || name === 'Unassigned') return <em style={{ color: '#9ca3af' }}>Unassigned</em>;
                            return (
                              <span style={{ backgroundColor: '#e0f2fe', color: '#0369a1', padding: '0.15rem 0.4rem', borderRadius: '0.25rem', fontSize: '0.75rem', fontWeight: 500 }}>
                                {name}
                              </span>
                            );
                          }
                        })()}
                      </div>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }} data-label="Actions">
                      <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center" }} onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => openTaskStatusModal(id)}
                          style={{ padding: '0.5rem', borderRadius: '0.5rem', border: '1px solid #d1d5db', backgroundColor: 'white', color: '#3b82f6', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          title="Change Status"
                        >
                          <Sparkles size={16} />
                        </button>
                        {status !== 'Completed' && status !== 'Late Completed' && (
                          <button
                            onClick={() => { if (window.confirm('Are you sure you want to delete this task?')) handleDeleteTask(id); }}
                            style={{ padding: '0.5rem', borderRadius: '0.5rem', border: '1px solid #fee2e2', backgroundColor: 'white', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            title="Delete Task"
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showTaskModal && selectedTask && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, padding: '1rem' }}>
          <div style={{ background: 'white', borderRadius: '1rem', width: '100%', maxWidth: '760px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {/* Fixed Header */}
            <div style={{ padding: '1.25rem', paddingBottom: '1rem', borderBottom: '1px solid #e5e7eb', background: 'white' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: '#111827', flex: 1 }}>{selectedTask.title || 'Task Details'}</h3>
                <button onClick={() => setShowTaskModal(false)} style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: '0.5rem', padding: '0.5rem 1rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500, flexShrink: 0 }}>Close</button>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.75rem' }}>
                <span style={getStatusBadgeStyle(calculateDynamicStatus(selectedTask))}>
                  {calculateDynamicStatus(selectedTask) === 'Pending' && ' '}
                  {calculateDynamicStatus(selectedTask) === 'In Progress' && '„ '}
                  {calculateDynamicStatus(selectedTask) === 'Completed' && '✅ '}
                  {calculateDynamicStatus(selectedTask) === 'Late Completed' && ' '}
                  {calculateDynamicStatus(selectedTask) === 'Overdue' && '⚠️ '}
                  {calculateDynamicStatus(selectedTask)}
                </span>
                <button onClick={(e) => { e.stopPropagation(); openTaskStatusModal(selectedTask._id || selectedTask.id); }} style={{ padding: '0.4rem 0.75rem', background: '#2563eb', color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>Change Status</button>
                {/* Delete button - only for non-completed tasks */}
                {calculateDynamicStatus(selectedTask) !== 'Completed' && calculateDynamicStatus(selectedTask) !== 'Late Completed' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm('Are you sure you want to delete this task?')) {
                        handleDeleteTask(selectedTask._id || selectedTask.id);
                      }
                    }}
                    style={{ padding: '0.4rem 0.75rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
                    —ï Delete
                  </button>
                )}
              </div>
            </div>

            {/* Scrollable Content */}
            <div style={{ padding: '1.25rem', overflow: 'auto', flex: 1 }}>
              <div style={{ display: 'grid', gap: '1rem' }}>

                {/* Point-wise info */}
                <div style={{ display: 'grid', gap: '0.5rem', padding: '0.75rem', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                  <div style={{ color: '#111827', fontWeight: 600 }}>Details</div>
                  <div style={{ display: 'grid', gap: 6 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <Target size={16} style={{ color: '#8b5cf6' }} />
                      <span><strong>Project:</strong> {selectedTask.projectName || getProjectNameForMilestone(selectedTask)}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <Calendar size={16} style={{ color: '#f59e0b' }} />
                      <span><strong>Due:</strong> {selectedTask.dueDate ? new Date(selectedTask.dueDate).toLocaleDateString('en-GB') : '—'}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <User size={16} style={{ color: '#8b5cf6' }} />
                      <span><strong>Assigned:</strong> {Array.isArray(selectedTask.assignedTeamMember)
                        ? selectedTask.assignedTeamMember.map(m => (m && typeof m === 'object' ? (m.teamMemberName || m.name) : (employees.find(e => e._id === m)?.teamMemberName || employees.find(e => e._id === m)?.name || 'Unknown'))).join(', ')
                        : (getEmployeeName(selectedTask.assignedTeamMember))}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <Building2 size={16} style={{ color: '#8b5cf6' }} />
                      <span><strong>Departments:</strong> {(() => {
                        const projectForTask = localProjects.find(p => (p.title === selectedTask.projectName) || (p._id === selectedTask.projectId) || (p.title === getProjectNameForMilestone(selectedTask)));
                        if (!projectForTask || !projectForTask.department) return '—';
                        if (Array.isArray(projectForTask.department)) {
                          return projectForTask.department.map(d => (typeof d === 'object' ? d.name : (departments.find(x => x._id === d || x.id === d)?.name || d))).join(', ');
                        }
                        return (typeof projectForTask.department === 'object' ? projectForTask.department.name : (departments.find(x => x._id === projectForTask.department || x.id === projectForTask.department)?.name || projectForTask.department));
                      })()}</span>
                    </div>
                  </div>
                </div>

                {/* Attachments */}
                <div style={{ padding: '0.75rem', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Attachments</div>
                  <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
                    {(() => {
                      // Collect all images from statusHistory
                      const allImages = [];
                      if (selectedTask?.statusHistory && Array.isArray(selectedTask.statusHistory)) {
                        selectedTask.statusHistory.forEach(entry => {
                          if (entry.images && Array.isArray(entry.images)) {
                            entry.images.forEach(img => {
                              const url = typeof img === 'string' ? img : (img.url || img.path || img.secure_url);
                              if (url) {
                                allImages.push({
                                  url: url.startsWith('/') ? `https://slateblue-mallard-430836.hostingersite.com/api${url}` : url,
                                  name: typeof img === 'string' ? img.split('/').pop() : (img.name || 'Image'),
                                  isImg: true
                                });
                              }
                            });
                          }
                        });
                      }

                      // Also check main task images/attachmentUrls
                      const mainImages = [...(Array.isArray(selectedTask?.images) ? selectedTask.images : []),
                      ...(Array.isArray(selectedTask?.attachmentUrls) ? selectedTask.attachmentUrls : [])];
                      mainImages.forEach(url => {
                        if (url && typeof url === 'string') {
                          allImages.push({
                            url: url.startsWith('/') ? `https://slateblue-mallard-430836.hostingersite.com/api${url}` : url,
                            name: url.split('/').pop() || 'Image',
                            isImg: /\.(png|jpe?g|gif|webp)$/i.test(url)
                          });
                        }
                      });

                      if (allImages.length === 0) return <div style={{ color: '#6b7280' }}>No attachments</div>;

                      return allImages.map((it, i) => {
                        if (it.isImg) {
                          return (
                            <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 8, textAlign: 'center', background: '#fff', cursor: 'zoom-in' }}>
                              <img
                                src={it.url}
                                alt={it.name}
                                style={{ width: '100%', height: 110, objectFit: 'cover', borderRadius: 6 }}
                                onClick={() => setImageViewer({ open: true, items: allImages.filter(x => x.isImg), index: allImages.filter(x => x.isImg).findIndex(x => x.url === it.url) })}
                              />
                            </div>
                          );
                        }
                        return (
                          <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 8, textAlign: 'center', background: '#fff' }}>
                            <a href={it.url} target="_blank" rel="noreferrer" style={{ display: 'block', color: '#2563eb', wordBreak: 'break-all' }}>{it.name}</a>
                          </div>
                        );
                      });
                    })()}
                  </div>
                  <div style={{ marginTop: 6, color: '#6b7280', fontSize: '0.8rem' }}>New uploads appear here after you update status.</div>
                </div>

                {/* Update History - Table with Rows */}
                <div style={{ padding: '0.75rem', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                  <div style={{ fontWeight: 600, marginBottom: 12, fontSize: '1rem' }}>Update History</div>
                  {(() => {
                    // Get all status history with descriptions and images
                    const history = selectedTask?.statusHistory || [];
                    const statusNotes = Array.isArray(selectedTask?.statusNote) ? selectedTask.statusNote :
                      (selectedTask?.statusNote ? [selectedTask.statusNote] : []);
                    const allImages = buildAttachmentItemsForTask(selectedTask).filter(it => it.isImg);

                    console.log('–ï Update History - All Images:', allImages.length, allImages);
                    console.log('📊 Status History:', history);

                    // Build update rows - combine status, description, and images
                    const updates = [];

                    // If we have history, use it (reverse to show latest first)
                    if (history.length > 0) {
                      const sortedHistory = [...history].reverse(); // Latest first
                      sortedHistory.forEach((historyItem, idx) => {
                        // Extract description from history item - Handle both string and array
                        let desc = 'No description';
                        if (typeof historyItem === 'object') {
                          if (historyItem.description) {
                            // Handle array or string description
                            if (Array.isArray(historyItem.description)) {
                              // Join array and remove square brackets
                              desc = historyItem.description.filter(d => d && typeof d === 'string').join('\n');
                            } else if (typeof historyItem.description === 'string') {
                              // Remove square brackets if present in string
                              desc = historyItem.description.replace(/^\[|\]$/g, '').trim();
                            }
                          } else if (historyItem.notes) {
                            // Fallback to notes field
                            if (Array.isArray(historyItem.notes)) {
                              desc = historyItem.notes.filter(d => d && typeof d === 'string').join('\n');
                            } else if (typeof historyItem.notes === 'string') {
                              desc = historyItem.notes.replace(/^\[|\]$/g, '').trim();
                            }
                          }
                        }

                        // Final cleanup: remove any remaining brackets
                        if (desc && desc !== 'No description') {
                          desc = desc.replace(/^\[|\]$/g, '').trim();
                        }

                        // Extract images from this specific history entry
                        let imgs = [];
                        if (typeof historyItem === 'object' && historyItem.images && historyItem.images.length > 0) {
                          // History item has its own images
                          const histImages = Array.isArray(historyItem.images) ? historyItem.images : [historyItem.images];

                          // Normalize image URLs - use localhost if running locally
                          const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
                          const API_BASE = isLocalhost ? 'https://slateblue-mallard-430836.hostingersite.com/api' : 'https://slateblue-mallard-430836.hostingersite.com/api';

                          imgs = histImages.map(img => {
                            if (typeof img === 'string') {
                              const url = img.startsWith('/') ? `${API_BASE}${img}` : img;
                              return { url, name: img.split('/').pop(), isImg: true };
                            } else if (img && typeof img === 'object') {
                              let url = img.url || img.path || img.location || img.secure_url || '';
                              if (url && url.startsWith('/')) url = `${API_BASE}${url}`;
                              return { url, name: img.name || url.split('/').pop(), isImg: true };
                            }
                            return null;
                          }).filter(Boolean);
                        }

                        // FALLBACK: If this is the first (latest) row and no images in history, show all task images
                        if (imgs.length === 0 && idx === 0 && allImages.length > 0) {
                          imgs = allImages;
                          console.log(` Row ${idx} - Using fallback: showing all ${allImages.length} task images`);
                        }

                        console.log(` Row ${idx} (${typeof historyItem === 'object' ? historyItem.status : historyItem}) - Images:`, imgs.length, imgs);

                        updates.push({
                          status: typeof historyItem === 'object' ? historyItem.status : historyItem,
                          timestamp: typeof historyItem === 'object' ? historyItem.timestamp : null,
                          description: desc,
                          images: imgs
                        });
                      });
                    } else {
                      // If no history, show current status
                      let currentDesc = 'No description';
                      if (statusNotes.length > 0) {
                        currentDesc = statusNotes.join('\n');
                      } else if (selectedTask?.description) {
                        if (Array.isArray(selectedTask.description)) {
                          currentDesc = selectedTask.description.join('\n');
                        } else {
                          currentDesc = String(selectedTask.description);
                        }
                      }

                      updates.push({
                        status: selectedTask?.status || 'Pending',
                        timestamp: null,
                        description: currentDesc,
                        images: allImages
                      });
                    }

                    if (updates.length === 0) {
                      return <div style={{ color: '#6b7280', fontSize: '0.9rem', textAlign: 'center', padding: '1rem' }}>No updates yet</div>;
                    }

                    return (
                      <div style={{ background: '#fff', borderRadius: 8, overflow: 'hidden' }}>
                        {/* Table Header */}
                        <div style={{ display: 'grid', gridTemplateColumns: '25% 45% 30%', gap: '0.5rem', padding: '0.75rem', background: '#f3f4f6', borderBottom: '2px solid #e5e7eb', fontWeight: 600, fontSize: '0.875rem' }}>
                          <div style={{ color: '#2563eb' }}>📊 Status</div>
                          <div style={{ color: '#059669' }}> Description</div>
                          <div style={{ color: '#dc2626' }}>–ï Images</div>
                        </div>

                        {/* Table Rows */}
                        <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                          {updates.map((update, idx) => (
                            <div key={idx} style={{ display: 'grid', gridTemplateColumns: '25% 45% 30%', gap: '0.5rem', padding: '0.75rem', borderBottom: idx < updates.length - 1 ? '1px solid #e5e7eb' : 'none', alignItems: 'start' }}>
                              {/* Status Cell */}
                              <div>
                                <div style={{ fontWeight: 600, color: '#111827', fontSize: '0.875rem' }}>
                                  {update.status === 'Pending' && ' '}
                                  {update.status === 'In Progress' && '„ '}
                                  {update.status === 'Completed' && '✅ '}
                                  {update.status === 'Late Completed' && ' '}
                                  {update.status === 'Overdue' && '⚠️ '}
                                  {update.status}
                                </div>
                                {update.timestamp && (
                                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 2 }}>
                                    {new Date(update.timestamp).toLocaleString('en-GB')}
                                  </div>
                                )}
                              </div>

                              {/* Description Cell */}
                              <div style={{ fontSize: '0.875rem', color: '#374151', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {update.description}
                              </div>

                              {/* Images Cell */}
                              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                {update.images && update.images.length > 0 ? (
                                  update.images.slice(0, 3).map((img, imgIdx) => {
                                    const imgUrl = typeof img === 'string' ? img : img.url;
                                    return (
                                      <div key={imgIdx} style={{ width: '50px', height: '50px', border: '1px solid #e5e7eb', borderRadius: 4, overflow: 'hidden', cursor: 'pointer' }}
                                        onClick={() => setImageViewer({ open: true, items: update.images.map(i => ({ url: typeof i === 'string' ? i : i.url, name: 'Image', isImg: true })), index: imgIdx })}>
                                        <img src={imgUrl} alt="Update" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                      </div>
                                    );
                                  })
                                ) : (
                                  <div style={{ color: '#9ca3af', fontSize: '0.75rem' }}>No images</div>
                                )}
                                {update.images && update.images.length > 3 && (
                                  <div style={{ width: '50px', height: '50px', border: '1px solid #e5e7eb', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f3f4f6', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280' }}>
                                    +{update.images.length - 3}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Task Status Modal (main view) */}
      {showStatusModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: '1rem' }}>
          <div style={{ background: 'white', borderRadius: '1rem', padding: '1.25rem', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', position: 'sticky', top: 0, background: 'white', zIndex: 1, paddingBottom: '0.5rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>Change Task Status</h3>
              <button onClick={() => setShowStatusModal(false)} style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: '0.5rem', padding: '0.5rem 1rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500 }}>Close</button>
            </div>
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Status</label>
                <select value={statusForm.status} onChange={(e) => setStatusForm(prev => ({ ...prev, status: e.target.value }))} style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.5rem' }}>
                  <option value="">-- Select Status --</option>
                  <option value="Pending">Pending</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                  <option value="Overdue">Overdue</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Description</label>
                <textarea rows={4} value={statusForm.description} onChange={(e) => setStatusForm(prev => ({ ...prev, description: e.target.value }))} style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', resize: 'vertical' }} placeholder="Write something about this status update..." />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Upload Images</label>
                <input
                  type="file"
                  multiple
                  accept="image/*,.jpg,.jpeg,.png,.gif,.webp"
                  onChange={handleStatusFileChange}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.5rem' }}
                />
                {statusForm.files?.length > 0 && (
                  <ul style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#374151' }}>
                    {statusForm.files.map((f, i) => (
                      <li key={i}>
                        {f.name} ({Math.round(f.size / 1024)} KB)
                      </li>
                    ))}
                  </ul>
                )}
                <div style={{ marginTop: 6, color: '#6b7280', fontSize: '0.8rem' }}>
                  You can upload multiple images. Supported formats: JPG, JPEG, PNG, GIF, WEBP
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => setShowStatusModal(false)}
                  style={{ padding: '0.5rem 0.9rem', borderRadius: '0.5rem', border: '1px solid #e5e7eb', background: 'white', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    console.log('„ Update button clicked');
                    try {
                      await submitTaskStatusChange();
                      // Success handled inside submitTaskStatusChange
                    } catch (error) {
                      console.error('Update button error:', error);
                      // Error already handled in submitTaskStatusChange
                    }
                  }}
                  disabled={uploadingMedia}
                  style={{
                    padding: '0.5rem 0.9rem',
                    borderRadius: '0.5rem',
                    background: uploadingMedia ? '#93c5fd' : '#2563eb',
                    color: 'white',
                    border: 'none',
                    cursor: uploadingMedia ? 'not-allowed' : 'pointer'
                  }}
                >
                  {uploadingMedia ? 'Updating...' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {imageViewer.open && imageViewer.items.length > 0 && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300 }}>
          <button
            onClick={() => setImageViewer({ open: false, items: [], index: 0 })}
            style={{ position: 'absolute', top: 16, right: 16, background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 12px', cursor: 'pointer' }}
          >
            Close
          </button>
          <div style={{ position: 'relative', width: '90%', maxWidth: 900, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <button
              onClick={() => setImageViewer(v => ({ ...v, index: Math.max(0, v.index - 1) }))}
              disabled={imageViewer.index === 0}
              style={{ position: 'absolute', left: -8, background: 'rgba(255,255,255,0.2)', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 14px', cursor: imageViewer.index === 0 ? 'not-allowed' : 'pointer' }}
            >

            </button>
            <img
              src={imageViewer.items[imageViewer.index].url}
              alt={imageViewer.items[imageViewer.index].name}
              style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 8, boxShadow: '0 10px 25px rgba(0,0,0,0.4)' }}
            />
            <button
              onClick={() => setImageViewer(v => ({ ...v, index: Math.min(v.items.length - 1, v.index + 1) }))}
              disabled={imageViewer.index === imageViewer.items.length - 1}
              style={{ position: 'absolute', right: -8, background: 'rgba(255,255,255,0.2)', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 14px', cursor: imageViewer.index === imageViewer.items.length - 1 ? 'not-allowed' : 'pointer' }}
            >

            </button>
          </div>
          <div style={{ position: 'absolute', bottom: 20, color: '#fff', textAlign: 'center' }}>
            <div style={{ marginBottom: 8 }}>{imageViewer.items[imageViewer.index].name || 'image'}</div>
            <a
              href={imageViewer.items[imageViewer.index].url}
              download
              target="_blank"
              rel="noreferrer"
              style={{ background: '#2563eb', color: '#fff', padding: '6px 10px', borderRadius: 6 }}
            >
              Download
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default FollowUpTasks;


